import { put, takeEvery, call, select, all, take, delay, spawn } from 'redux-saga/effects';
import * as app from '../app';
import * as auth from '../auth';
import * as router from '../router';
import * as signup from '../signup';
import * as analytics from '../../analytics/analytics.js';
import * as knownFeaturesSelectors from '../known-features/selectors.js';
import * as knownFeaturesActions from '../known-features/actions.js';
import * as boardSummary from '../board-summary';
import * as boardExplorerActions from '../board-explorer/actions.js';
import * as createBoardSelectors from '../board-explorer/create-board/selectors.js';
import firestoreRedux from '@dreamworld/firestore-redux';
import entityIdProvider from '../../entity-id-provider';
import { requestApi, isNetworkError } from "../../request-api"
import { membersList, knownUsers } from './selectors';
import filter from 'lodash-es/filter';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import without from 'lodash-es/without';
import forEach from 'lodash-es/forEach';
import find from 'lodash-es/find';
import forEachRight from 'lodash-es/forEachRight';
import map from 'lodash-es/map';
import cloneDeep from 'lodash-es/cloneDeep';
import merge from 'lodash-es/merge';
import { show as showSnackBar, hide as hideSnackBar } from '../../components/kerika-snackbar';
import i18next from '@dw/i18next-esm';
import { store } from '../../store';
import { ReduxUtils } from '@dw/pwa-helpers/redux-utils';
import { favoriteBoard, accessibleBoards } from '../../redux/board-explorer/selectors';
import { getIdWoPrefix, resolveToolDueTime } from '../../utils';
import { userIdByEmail } from './utils.js';
import { toEditorValue } from '../references/utils.js';
import { titleOf } from '../references/anchors.js';
import { getUserName } from '../../components/utils.js';

import * as actions from './actions';
import * as selectors from './selectors';
import * as columns from '../columns/selectors.js';
import { getInfo } from '../../link-resolver/index.js';
import { urlSubType } from '../attachments/utils.js';
import * as unreadHighlightsActions from '../unread-highlights/actions.js';
import * as subscriptionV2Actions from '../subscription-v2/actions.js';
import { markdownToHtml } from '../../utils/markdown-to-html.js';
import * as manageAccountSelectors from '../manage-account/selectors.js';
import { queryBoardCollections as queryAiTeamMemberCollections } from '../ai-agent-status/saga.js';

function* loadCurrentBoardDetail() {
  let state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const accountId = router.selectors.accountId(state);
  const accessibleAccounts = auth.selectors.accessibleAccounts(state);
  const loadAccountTeam = accessibleAccounts && accessibleAccounts.includes(accountId);
  const userId = auth.selectors.currentUserId(state);
  const type = get(state, 'router.page.params.type');
  const privatePage = get(state, 'router.page.params.private');

  const dialogName = router.selectors.dialogName(state);

  // Load AI pending edits BEFORE the early return below, so landing directly on a card
  // (CARD dialog) — including a cross-board card opened from another board's panel — still
  // materialises its proposed changes + diff. A card dialog implies a task board, so gate on
  // TASKBOARD or an open CARD; guarded so it doesn't double-spawn when the full board detail
  // also loads.
  if (boardId && userId && (type === 'TASKBOARD' || dialogName === 'CARD') && !aiPendingEditsUnsubscribers[boardId] && !aiPendingEditsLoading[boardId]) {
    yield spawn(loadAiPendingEdits, { boardId, userId });
  }

  if (privatePage) {
    return;
  }

  // Previously this also bailed whenever a card dialog was open, assuming the
  // background board was either already loaded (you opened the card from it) or
  // not shown until the card closed. Card stacking broke both assumptions: a
  // linked card opened over another board — cross-account, or a search jump —
  // makes the background (page) board a board that was never loaded, yet it's
  // rendered behind the card, so it must be loaded now or the background shows
  // blank. This is only reached when the board page element is (re)created (i.e.
  // the page board changed), by which point the previous board is already
  // unsubscribed, so there's no redundant reload of an already-live board.
  if (!boardId || !accountId) {
    throw new Error('Board or Account id is not found.');
  }

  try {
    let allPromises = [];
    console.time(`Board Load Time : ${boardId}`);

    //Loads login user's card summary for current board.
    if(userId && type === 'TASKBOARD') {
      try {
        const cardSummaryPromise = requestApi(`/card/cards/card-summary?boardId=${boardId}`, {excludeErrors: [403]});
        const columnSummaryPromise = requestApi(`/card/cards/column-summary?boardId=${boardId}`, {excludeErrors: [403]});
        allPromises.push(cardSummaryPromise, columnSummaryPromise);
      } catch (error) {}
    }

    const requesterId = `board-page-${boardId}`;

    // Loads board attrs.
    const attrQuery = firestoreRedux.getDocById('boards', boardId, { requesterId });
    let attrs = selectors.attrs(state, boardId);
    if(isEmpty(attrs)) {
      allPromises.push(attrQuery.waitTillResultAvailableInState());
    }

    // Loads columns & tags.
    if (type === 'TASKBOARD') {
      const columnsquery = firestoreRedux.query('columns', { id: `board_columns_${boardId}`, where: [['boardId', '==', boardId]], requesterId });
      if(userId){
        const hiddenColumnsQuery = firestoreRedux.query('hidden-columns', { id: `hidden-columns-${boardId}`, where: [['boardId', '==', boardId], ['userId', '==', userId]], requesterId });
        if(isEmpty(firestoreRedux.selectors.queryResult(state, `hidden-columns-${boardId}`))) {
          allPromises.push(hiddenColumnsQuery.waitTillResultAvailableInState());
        }
      }
      if(isEmpty(firestoreRedux.selectors.queryResult(state, `board_columns_${boardId}`))){
        allPromises.push(columnsquery.waitTillResultAvailableInState());
      }

      firestoreRedux.query('board-tags', { id: `board-tags-${boardId}`, where: [['boardId', '==', boardId]], requesterId });
      firestoreRedux.getDocById('tag-colors', 'tc_0', { requesterId });
    }

    // Loads board team membes.
    const teamQuery = firestoreRedux.query('board-team-members', { id: `board-team-members_boardId-${boardId}`, where: [['boardId', '==', boardId]], requesterId });
    if(isEmpty(firestoreRedux.selectors.queryResult(state, `board-team-members_boardId-${boardId}`))){
      allPromises.push(teamQuery.waitTillResultAvailableInState()); //Note: wait till result is updated in redux state.
    }

    // AI Team Member card statuses and board instructions; not waited for.
    yield spawn(queryAiTeamMemberCollections, {
      boardId,
      requesterId,
      collections: ['ai-agent-card-statuses', 'ai-agent-board-profiles'],
      accountIdOfBoard: () => accountId,
    });

    //Load use case board deatials
    const useCaseBoardQuery = firestoreRedux.getDocById('use-case-boards', `ucb_${getIdWoPrefix({ id: boardId, prefix: 'brd_' })}`);
    allPromises.push(useCaseBoardQuery);

    // Loads all cards
    const cardsQueryId = `cards_${boardId}`;
    const cardsQuery = firestoreRedux.query('cards', { id: cardsQueryId, where: [['boardId', '==', boardId], ['columnType', '==', 'OTHER']], requesterId });
    const cardsQueryResult = firestoreRedux.selectors.queryResult(state, cardsQueryId);
    if(isEmpty(cardsQueryResult)){
      allPromises.push(cardsQuery.waitTillResultAvailableInState());
    }

    // Card summary
    const cardsSummaryQueryId = `cards_summary_${boardId}`;
    const cardsSummaryQuery = firestoreRedux.query('cards-summary', { id: cardsSummaryQueryId, where: [['boardId', '==', boardId], ['columnType', '==', 'OTHER']], orderBy: [['columnOrder']], requesterId });
    const cardsSummaryQueryResult = firestoreRedux.selectors.queryResult(state, cardsSummaryQueryId);
    if(isEmpty(cardsSummaryQueryResult)) {
      allPromises.push(cardsSummaryQuery.waitTillResultAvailableInState());
    }

    if(userId) {
      //Loads user board-ui details
      const userBoardUiQuery = firestoreRedux.query('user-board-ui', { id: `user-board-ui_${boardId}_${userId}`, where: [['boardId', '==', boardId], ['userId', '==', userId]], requesterId });
      if(isEmpty(firestoreRedux.selectors.queryResult(state, `user-board-ui_${boardId}_${userId}`))){
        allPromises.push(userBoardUiQuery.waitTillResultAvailableInState());
      }

      // Loads user unread details. 
      firestoreRedux.query('card-unread-user-details', { id: `board_card_unread_${boardId}`, where: [['boardId', '==', boardId], ['userId', '==', userId], ['anyUnread', '==', true]], requesterId });
      firestoreRedux.query('column-unread-user-details', { where: [['boardId', '==', boardId], ['userId', '==', userId], ['anyUnread', '==', true]], requesterId });
    }

    // Subscribe account / board team members & loads its attrs.
    yield call(subscribeBoardMembers, { boardId, loadAccountTeam });
    yield call(subscribeAccountId);

    const allPromiseResult = yield Promise.all(allPromises);
    attrs = attrs || get(allPromiseResult, (userId && type === 'TASKBOARD') ? '2' : '0');

    // Gets columns & cards summary from APIs responses.
    let columnSummary = {};
    let boardCardSummary = {};
    if(userId && type === 'TASKBOARD'){
      boardCardSummary = get(allPromiseResult, '0');
      columnSummary = get(allPromiseResult, '1');
    }

    state =  yield select();
    const doneColumnId = columns.currentBoardDoneColumnId(state);
    const trashColumnId = columns.currentBoardTrashColumnId(state);

    const _hasImportBoardStatus = selectors.hasImportBoardStatus(state, boardId);
    if(_hasImportBoardStatus) {
      yield put(actions.markImportBoardStatusAck(boardId));
    }

    firestoreRedux.getDocById(`boards/${boardId}/columns-summary`, `cos_${getIdWoPrefix({id: doneColumnId, prefix: 'col_'})}`, { requesterId });
    firestoreRedux.getDocById(`boards/${boardId}/columns-summary`, `cos_${getIdWoPrefix({id: trashColumnId, prefix: 'col_'})}`, { requesterId });

    console.timeEnd(`Board Load Time : ${boardId}`);
    yield put({ type: boardSummary.actions.LOAD_COLUMN_SUMMARY_DONE, summary: columnSummary, id: boardId });

    //Dispatch action to mark board as loaded on redux state
    yield put(actions.boardLoaded(boardId, boardCardSummary));

    // Track analytics events.
    if(attrs && attrs.template) {
      analytics.logTemplateEvent('template opened', {}, { trackReferrer: true });
    } else {
      analytics.logBoardEvent('board opened', {});
    }

    // Done & Trash Cards & summary
    if (type === 'TASKBOARD') {
      firestoreRedux.query('cards', { id: `done_cards_${boardId}`, where: [['boardId', '==', boardId], ['columnType', '==', 'DONE']], limit: 100, orderBy: [['columnOrder']], requesterId });
      firestoreRedux.query('cards', { id: `trash_cards_${boardId}`, where: [['boardId', '==', boardId], ['columnType', '==', 'TRASH']], limit: 100, orderBy: [['columnOrder']], requesterId });
      firestoreRedux.query('cards-summary', { id: `done_cards_summary_${boardId}`, where: [['boardId', '==', boardId], ['columnType', '==', 'DONE']], limit: 100, orderBy: [['columnOrder']], requesterId });
      firestoreRedux.query('cards-summary', { id: `trash_cards_summary_${boardId}`, where: [['boardId', '==', boardId], ['columnType', '==', 'TRASH']], limit: 100, orderBy: [['columnOrder']], requesterId });
    }

    if(loadAccountTeam) {
      firestoreRedux.query('account-team-members', { id: `account-team-members-${accountId}`, where: [['accountId', '==', accountId]], requesterId });
    }

    // Loads board-move-requests.
    if(!isEmpty(attrs)) {
      const bmrDocId = `bmr_${getIdWoPrefix({id: boardId, prefix: 'brd_'})}`;
      const boardMoveRequestsQuery = firestoreRedux.getDocById('board-move-requests', bmrDocId, { requesterId });
      const boardMoveRequests = yield boardMoveRequestsQuery.result;
      if(boardMoveRequests && boardMoveRequests.movingTo) {
        firestoreRedux.getDocById('users', boardMoveRequests.movingTo, { requesterId });
      }
    }

    if(userId) {
      const boardUnreadQuery = firestoreRedux.query('board-unread-user-details', { id: `board-unread-user-details-${boardId}`, where: [['boardId', '==', boardId], ['userId', '==', userId], ['anyUnread', '==', true]], requesterId });
      const boardUnreadResults = yield boardUnreadQuery.result;
      const boardUnread = boardUnreadResults && boardUnreadResults[0] || {};
      if(boardUnread && boardUnread.anyUnread && boardUnread.unread && (boardUnread.unread.name || boardUnread.unread.created)) {
        let attributes = [];
        if(boardUnread.unread.name) {
          attributes.push('name');
        }

        if(boardUnread.unread.created) {
          attributes.push('created');
        }
        setTimeout(() => {
          if(boardId) {
            store.dispatch(unreadHighlightsActions.markAsRead(boardId, attributes));
          }
        }, 3000);
      }
    }
  } catch (error) {
    if(error && error.status === 403) {
      yield put(router.actions.updatePrivateBoard(true));
      return;
    }

    const errorString = error && error.toString && error.toString() || '';
    if (error && ((error.includes && error.includes('permission-denied')) || (errorString && errorString.includes('permission-denied')))) {
      yield put(router.actions.updatePrivateBoard(true));
    }
    console.error("current board details is not loaded due to this: ", error);
  }
}

/**
 * Show toast if member already exist.
 * Send request to add member.
 * Show toast after successfull add. If user is knownUser, show her name otherwise her email in toast.
 * @param {Object} action action payload e.g({type: 'BOARD_TEAM_ADD_MEMBER', requestId, boardId, email, role})
 */
function* addMember(action) {
  const emails = action.emails || [];
  const role = action.role || '';
  yield put({ type: actions.BOARD_TEAM_ADD_MEMBER_IN_PROGRESS, requestId: action.requestId, boardId: action.boardId, emails, role });
  const state = yield select();
  const members = membersList(state, action.boardId);
  const filteredMember = filter(members, (obj) => {
    const email = obj.user && obj.user.email;
    return emails.includes(email);
  });

  if (filteredMember && filteredMember.length) {
    yield put({ type: actions.BOARD_TEAM_ADD_MEMBER_FAILED, requestId: action.requestId, failureReason: 'ALREADY_EXIST', emails, role });
    return;
  }

  const body = [];
  forEach(emails, (email) => {
    body.push({email, role});
  });

  let config = { excludeErrors: [502, 401, 409, 400], method: 'POST', body };
  try {
    store.dispatch(knownFeaturesActions.markAsKnown('SHARE_BOARD'));
    const response = yield call(requestApi, `/board/boards/${action.boardId}/team`, config);
    analytics.logBoardEvent('board team member added', {'user_role': role, invited: response && response.invited || false});

    const known_users = knownUsers(state);
    const filteredKnownUser = filter(known_users, user => emails.includes(user.email));

    let toastUserNames = [];
    forEach(emails, (email) => {
      let name = email;
      forEach(filteredKnownUser, (user) => {
        if(user && user.email === email) {
          name = user.name || user.email;
          return false;
        }
      })
      toastUserNames.push(name);
    });

    if(toastUserNames && toastUserNames.length > 1) {
      toastUserNames[toastUserNames.length - 1] = i18next.t('seprated.and') + ' ' + toastUserNames[toastUserNames.length - 1];
    }
    let toastUserName = toastUserNames && toastUserNames.length > 1 ? toastUserNames.join(', '): toastUserNames[0];
    yield put({ type: actions.BOARD_TEAM_ADD_MEMBER_DONE, requestId: action.requestId, tip: knownFeaturesSelectors.boardMemberAddedTipOpened(state) ? 'NEW_MEMBER_ADDED': '', pendingMemberTip: knownFeaturesSelectors.pendingMemberAssignableTipOpened(state), emails, role});
    if(emails && emails.length > 1) {
      showSnackBar({ message: i18next.t('board-page:teamToasts.multipleUserAdded', { userNames: toastUserName }) });
    } else {
      showSnackBar({ message: i18next.t('board-page:teamToasts.added', { userName: toastUserName }) });
    }
  } catch (err) {
    const errorCode = err && err.code || 'UNKNOWN';
    yield put({ type: actions.BOARD_TEAM_ADD_MEMBER_FAILED, requestId: action.requestId, failureReason: errorCode, emails, role });

    if (errorCode === 'SUBSCRIPTION_NOT_FOUND') {
      if(!app.selectors.isInstalledApp(state)) {
        yield call(router.actions.removeActionParam, 'board-team', {'board-team-action': 'add'});
        const data = { boardId: action.boardId, emails, role, action: 'add', type: 'board-team', as: emails && emails.length || 1, requestId: action.requestId };
        yield call(router.actions.openManageTeamConfirmDialog, data);
      }
      return;
    }

    if (errorCode === 'BLACKLIST_DOMAIN_NOT_ALLOWED') {
      const errorMessage = err.message || '';
      const domainRegex = /^([\w.-]+\.\w{2,})(\s*,\s*[\w.-]+\.\w{2,})*$/;
      let domain = '';
      if (domainRegex.test(errorMessage.trim())) {
        domain = errorMessage.trim();
      } else {
        domain = emails && emails.length ? emails[0].split('@')[1] : '';
      }
      
      yield call(router.actions.openBlackListDomainInfoDialog, { domain });
      return;
    }

    console.error('Board team > add member failed due to this: ', err);
  }
}

/**
 * Change board member role.
 * When `SUBSCRIPTION_NOT_FOUND`, show subscription dialog.
 * @param {Object} action action payload. e.g({type: 'BOARD_TEAM_CHANGE_MEMBER_ROLE', boardId, userId, role})
 */
function* changeMemberRole(action) {
  analytics.logBoardEvent('board team member role changed');
  const state = yield select();
  const isBoardOwner = selectors.isBoardOwner({state, boardId: action.boardId, userId: action.userId});
  const currentRole = selectors.boardRole({state, boardId: action.boardId, userId: action.userId});
  const newRole = action.role;
  const upgrade = currentRole === 'VISITOR' && (newRole === 'BOARD_ADMIN' || newRole === 'TEAM_MEMBER');
  const downgrade = newRole === 'VISITOR' && (currentRole === 'BOARD_ADMIN' || currentRole === 'TEAM_MEMBER');
  const tip = isBoardOwner ? '': upgrade && knownFeaturesSelectors.boardMemberUpgradedTipOpened(state) ? 'MEMBER_ROLE_UPGRADE': downgrade && knownFeaturesSelectors.boardMemberDowngradedTipOpened(state) ? 'MEMBER_ROLE_DOWNGRADE': '';
  try {
    const config = { excludeErrors: [502, 401, 409], method: 'PUT', body: { role: newRole } };
    yield call(requestApi, `/board/boards/${action.boardId}/team/${action.userId}`, config);
    yield put({ type: actions.BOARD_TEAM_CHANGE_MEMBER_ROLE_DONE, boardId: action.boardId, userId: action.userId, role: newRole, upgrade, tip, downgrade });
  } catch (err) {
    const code = err && err.code || 'UNKNOWN';
    yield put({ type: actions.BOARD_TEAM_CHANGE_MEMBER_ROLE_FAILED, boardId: action.boardId, userId: action.userId, role: newRole, error: code, upgrade, downgrade });
    if (err.code === 'SUBSCRIPTION_NOT_FOUND' && !app.selectors.isInstalledApp(state)) {
      yield call(router.actions.removeActionParam, 'board-team', {'board-team-action': 'add'});
      const data = { boardId: action.boardId, memberId: action.userId, role: newRole, action: 'upgrade', type: 'board-team', as: 1};
      yield call(router.actions.openManageTeamConfirmDialog, data);
    }
  }
}

/**
 * Remove member from team
 * When user leaves team redirect her to home page.
 * @param {Object} action action payload. e.g({type: 'BOARD_TEAM_REMOVE_MEMBER', boardId, userId, useName})
 */
function* removeMember(action) {
  if(action.leave) {
    analytics.logBoardEvent('board team leave');
  } else {
    analytics.logBoardEvent('board team member removed');
  }

  const state = yield select();
  const boardId = action.boardId;
  const userId = action.userId;
  const members = selectors.boardTeamMembersDoc(state, boardId) || [];
  const currentDoc = members.find(member => member.userId === userId);
  const localWritePath = `boards/${boardId}/board-team-members`;
  try {
    firestoreRedux.save(localWritePath, {...currentDoc, ...{ removed: true }}, { localWrite: true });
    let config = { excludeErrors: [502, 401], method: 'DELETE' };
    yield call(requestApi, `/board/boards/${boardId}/team/${userId}`, config);

    //Member removed tip-acknowledged
    if(action.tipAknowledged) {
      yield put(knownFeaturesActions.markAsKnown('MEMBER_REMOVED'));
    }
    const state = yield select();
    const currentUser = auth.selectors.currentUserId(state);
    const currentAccountId = router.selectors.accountId(state);
    const currentUserAccountId = get(auth.selectors.currentUserOwnedAccounts(state), '0');
    const pageName = router.selectors.page(state).name;
    if (action.userId == currentUser && currentAccountId !== currentUserAccountId && pageName === 'BOARD') {
      router.actions.navigate(`/${currentAccountId}/home/favorite/boards`, true);
    }
  } catch (error) {
    firestoreRedux.save(localWritePath, currentDoc, { localWrite: true });
    if(isNetworkError(error) || error.code === 504){
      return;
    }
    console.error('Board member removed > failed due to this: ', error);
  }

}

/**
 * Loads known-users for current user.
 * Loads all known-users basic attrs.
 */
function* loadKnownUsers() {
  try {
    const knownUsers = yield call(requestApi, `/account/known-users`);
    forEach(knownUsers, (user) => {
      firestoreRedux.getDocById('users', user.id, { requesterId: `known-users` });
    })
    yield put({ type: actions.LOAD_KNOWN_USERS_DONE, knownUsers });
  } catch (error) {
    console.error(`Failed to load known users`, error);
  }
}

/**
 * Load subscription info for current account.
 * @param {Object} action Action payload
 */
function* loadAccountSubscription(action) {
  const state = yield select();
  const accountId = router.selectors.accountId(state);
  const currentUserId = auth.selectors.currentUserId(state);
  if (accountId && currentUserId) {
    subscribeAccountOwner(accountId);
    yield put(subscriptionV2Actions.load(accountId, action.requesterId));
    yield put(subscriptionV2Actions.loadPricing(accountId));
  }
}

let unsubscribeAccountOwnerHandlers = {};
/**
 * Subscibe on account subscription path & load account owner detail.
 * @param {Number} accountId account Id
 */
const subscribeAccountOwner = (accountId) => {
  unsubscribeAccountOwnerHandlers[accountId] && unsubscribeAccountOwnerHandlers[accountId]();
  unsubscribeAccountOwnerHandlers[accountId] = ReduxUtils.subscribe(store, `firestore.docs.subscriptions.sub_${getIdWoPrefix({ id: accountId, prefix: 'acc_' })}.ownerId`, (ownerId) => {
    if (ownerId) {
      firestoreRedux.getDocById('users', ownerId);
    }
  });
}

/**
 * Disconnet known-users from firestore.
 * @param {Object} action Action payload
 */
function* disconnectKnownUsers(action) {
}

/**
 *
 * @param {*} param0
 *  @property {String} requesterId Firestore query requester id.
 *  @property {String} boardId Board Id of which columns will be loaded.
 */
function* loadColumns({ requesterId, boardId }) {
  firestoreRedux.query('columns', { id: `board_columns_${boardId}`, where: [['boardId', '==', boardId]], requesterId });
}

/**
 *
 * @param {*} param0
 *  @property {String} requesterId Firestore query requester id.
 */
function* disconnectColumns({ requesterId }) {
  firestoreRedux.cancelQueryByRequester(requesterId);
}

/**
 * Updates board basic details.
 * @param {*} param0
 *  @property {String} name Field name which will be updated.
 *  @property {String} value New value of the given field.
 *  @property {String} boardId Board ID.
 */
function* updateBoardBasicDetail({ name, value, boardId }) {

  const state = yield select();
  const currDoc = firestoreRedux.selectors.doc(state, 'boards', boardId) || { id: boardId };
  let newDoc = cloneDeep(currDoc);
  newDoc = { ...newDoc, [name]: value };

  try {
    firestoreRedux.save('boards', newDoc, { localWrite: true });
    yield call(requestApi, `/board/boards/${boardId}/basic`, { method: 'PUT', body: newDoc });
    yield put({ type: actions.UPDATE_BASIC_DETAIL_DONE, boardId, name, value });
  } catch (err) {
    yield put({ type: actions.UPDATE_BASIC_DETAIL_FAILED, boardId, name, value });
    firestoreRedux.save('boards', currDoc, { localWrite: true });
    console.warn(err);
  }
}

/**
 * Favorites / unfavorites board.
 * @param {*} param0
 *  @property {String} boardId Board ID.
 *  @property {Boolean} hideToast When it's true, doesn't show the toast message.
 */
function* updateFavorite({ boardId, hideToast }) {
  try {
    const state = yield select();
    const favorite = !favoriteBoard(state, boardId);
    const isTemplate = selectors.isTemplate(state, boardId);
    const attrs = selectors.attrs(state, boardId);

    // Start localWrite.
    const accountId = get(attrs, 'accountId');
    const userId = auth.selectors.currentUserId(state);
    const allFavBoards = firestoreRedux.selectors.collection(state, 'favorite-boards');
    const currentFavBoardDoc = find(allFavBoards, { boardId });
    let id = get(currentFavBoardDoc, 'id');
    if (!id) {
      const uuid = entityIdProvider.getId();
      id = `ufb_${uuid}`;
    }
    const queryId = 'favorite-boards';
    if (favorite) {
      firestoreRedux.save('favorite-boards', { id, accountId, boardId, userId }, { localWrite: true, queryId });
    } else {
      firestoreRedux.delete('favorite-boards', id, { localWrite: true, queryId });
    }
    // End localWrite.

    if (!hideToast) {
      const toastText = isTemplate ? !favorite ? 'unfavoriteTemplate' : 'favoriteTemplate' : !favorite ? 'unfavoriteBoard' : 'favoriteBoard';
      yield call(showSnackBar, { id: boardId, message: i18next.t(`board-explorer:boardCard.toast.${toastText}`), actionButton: { caption: i18next.t('app:buttons.undo'), callback: ()=> { undoFavorite(boardId)}} });
    }
    yield call(requestApi, `/board/boards/${boardId}/favorite`, { method: 'PUT', body: { id, favorite } });
  } catch (err) {
    console.warn(err);
  }
}

/**
 * Mark welcomeDialogPresented as true.
 */
function* markWelcomeDialogAsPresented() {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);

  const presented = selectors.welcomeDialogPresented(state, boardId);
  if (presented) {
    return;
  }
  const queryId = `user-board-ui_${boardId}_${userId}`;
  firestoreRedux.save(`users/${userId}/user-board-ui`, {
    id: `buwd_${entityIdProvider.getId()}`,
    type: 'board-ui-welcome-dialog',
    userId,
    boardId,
    presented: true,
  },
  { queryId, localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
}

/**
 * Undo favorite action.
 * @param {Number} boardId Board Id
 */
function undoFavorite(boardId) {
  store.dispatch(actions.updateFavorite(boardId, true));
}

/**
 * Sends request to copy board.
 * @param {*} param0
 *  @property {String} name Board name.
 *  @property {String} accountId Destination account ID
 *  @property {String} boardId Board ID which is copied.
 *  @property {String} boardType Type of the board. e.g. 'TASKBOARD' or 'WHITEBOARD'
 */
 function* copy({ name, accountId, boardId, boardType }) {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  try {
    const response = yield call(requestApi, `/board/boards/${boardId}/copy`, {
      method: 'POST',
      body: { name, accountId },
      excludeErrors: [403],
    });

    analytics.logBoardCreatedEvent(response.id);

    const allUserAccUiDocs = firestoreRedux.selectors.collection(state, 'user-account-ui');
    const createBoardPreference = find(allUserAccUiDocs, { accountId, userId, type: 'account-ui-create-board-preferences' }) || {};
    const lastUsedTemplate = get(createBoardPreference, `lastUsedTemplate`, {});
    const id = get(createBoardPreference, 'id', `acbp_${entityIdProvider.getId()}`);
    // Saves last used board type & last used template into user-account-ui.
    firestoreRedux.save(`users/${userId}/user-account-ui`, {
      id,
      userId,
      accountId,
      type: 'account-ui-create-board-preferences',
      lastUsedType: boardType,
      lastUsedTemplate,
    }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });

    yield call(openBoard, response.id, accountId);
  } catch (err) {
    if (err && err.code === 'NOT_A_TEAM_MEMBER') {
      yield call(showSnackBar, { message: i18next.t('board-explorer:boardCard.toast.copyToOtherAccountNotAllowed'), type: 'ERROR' });
    }
    yield put({ type: actions.BOARD_COPY_FAILED, accountId, boardId });
    console.warn(err);
  }
}

function* createBoardAI({ prompt, durationType, teamSize, columnUsageType, accountId, boardId, boardPrivacy }) {
  let state = yield select();
  accountId = accountId || auth.selectors.currentUserOwnedAccountId(state);
  try {

    if(!accountId) {
      const user = auth.selectors.currentUser(state);
      const name = user ? `${getUserName(user)}'s Account`: '';
      yield put(signup.actions.createAccount(name));
      yield call(waitTillOwnedAccountAvailable);
      state = yield select();
      accountId = auth.selectors.currentUserOwnedAccountId(state);
    }

    boardPrivacy = boardPrivacy || createBoardSelectors.boardPrivacy(state, accountId);
    const response = yield call(requestApi, `/import-board/boards/ai-create`, {
      method: 'POST',
      body: { prompt, durationType, teamSize: null, columnUsageType, accountId, boardId, boardPrivacy },
      excludeErrors: [403],
    });

    const isOwnedAccount = auth.selectors.currentUserOwnedAccountId(state) === accountId;
    const eventProps = {
      type: 'TASKBOARD',
      privacy: boardPrivacy,
      account_id: accountId,
      in_own_account: isOwnedAccount
    };

    if (boardId) {
      eventProps['example_board_Id'] = boardId;
    }

    analytics.logEvent('create board ai initiated', eventProps);

    yield put(actions._createBoardAIDone({ prompt, durationType, teamSize, columnUsageType, accountId, boardId, boardPrivacy }));

    // Set signup details for fresh users who created their first board via AI.
    // When the account already exists at the time createBoardAI is called, the createAccount
    // saga is skipped, so updateSignupDetails is never called, leaving the user without a
    // signupSource. Without signupSource, isNewUser=false and onboarding tips are never shown.
    state = yield select();
    if (isEmpty(signup.selectors.signupDetails(state))) {
      yield put(signup.actions.updateSignupDetails('FRESH_USER', {accountId, boardId}));
    }
  } catch (err) {
    const code = err && err.code || 'UNKNOWN';
    yield put(actions._createBoardAIFailed({ accountId, prompt, durationType, teamSize, columnUsageType, error: code, boardId, boardPrivacy }));
    if(isNetworkError(err) || err.code === 504){
      return;
    }
    console.error("createBoardAI > failed due to this: ", err);
  }
}

function* createTemplateAI({ prompt, durationType, teamSize, columnUsageType, accountId, templateCategory, templatePrivacy }) {
  let state = yield select();
  accountId = accountId || auth.selectors.currentUserOwnedAccountId(state);
  try {

    if(!accountId) {
      const user = auth.selectors.currentUser(state);
      const name = user ? `${getUserName(user)}'s Account`: '';
      yield put(signup.actions.createAccount(name));
      yield call(waitTillOwnedAccountAvailable);
      state = yield select();
      accountId = auth.selectors.currentUserOwnedAccountId(state);
    }

    const isOwnedAccount = auth.selectors.currentUserOwnedAccountId(state) === accountId;
    analytics.logEvent('create template ai initiated', {
      'type': 'TASKBOARD',
      'privacy': templatePrivacy,
      'account_id': accountId,
      'in_own_account': isOwnedAccount,
      'category': templateCategory
    });

    templatePrivacy = templatePrivacy || createBoardSelectors.boardPrivacy(state, accountId);
    const response = yield call(requestApi, `/import-board/boards/ai-create`, {
      method: 'POST',
      body: { prompt, durationType, teamSize, columnUsageType, accountId, template: true, templateCategory, boardPrivacy: templatePrivacy },
      excludeErrors: [403],
    });

    yield put(actions._createTemplateAIDone({ prompt, durationType, teamSize, columnUsageType, accountId, templatePrivacy, templateCategory }));
  } catch (err) {
    const code = err && err.code || 'UNKNOWN';
    yield put(actions._createTemplateAIFailed({ 
      accountId, 
      prompt, 
      durationType, 
      teamSize, 
      columnUsageType, 
      templateCategory,
      error: code ,
      templatePrivacy
    }));
    if(isNetworkError(err) || err.code === 504){
      return;
    }
    console.error("createTemplateAI > failed due to this: ", err);
  }
}

function* useCaseBoardCopy({ accountId, boardId, copyTeam }) {
  let state = yield select();
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  const doc = selectors.usedUseCaseBoardsDoc(state);
  const userId = auth.selectors.currentUserId(state);
  const wideLayout = app.selectors.wideLayout(state);
  accountId = accountId || auth.selectors.currentUserOwnedAccountId(state);
  boardId = boardId || router.selectors.pageBoardId(state);
  copyTeam = copyTeam || false;
  analytics.logEvent('use-case board copied', {'boardId': boardId, copyTeam });
  try {
    yield put(knownFeaturesActions.markAsKnown(wideLayout ? 'MAKE_A_BOARD_OR_BROWSE_DESKTOP': 'MAKE_A_BOARD_OR_BROWSE_MOBILE'));
    router.actions.navigate('/use-case-board-copying');

    if(!accountId) {
      const user = auth.selectors.currentUser(state);
      const name = user ? `${getUserName(user)}'s Account`: '';
      yield put(signup.actions.createAccount(name));
      yield call(waitTillOwnedAccountAvailable);
      state = yield select();
      accountId = auth.selectors.currentUserOwnedAccountId(state);
    }

    const response = yield call(requestApi, `/board/boards/${boardId}/copy`, {
      method: 'POST',
      body: { accountId, copyTeam },
      excludeErrors: [403],
    });

    analytics.logBoardCreatedEvent(response.id);

    const boards = doc && doc.boards || {};

    // Saves last used account in user-ui.
    firestoreRedux.save(`users/${userId}/user-ui`, {
      id: `uucd_${getIdWoPrefix({id: userId, prefix: 'usr_'})}`,
      userId,
      type: 'used-use-case-boards',
      boards: {...boards, ...{[boardId]: response.id}}
    }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
    yield call(openBoard, response.id, accountId, 'copy-use-case-board', copyTeam);
  } catch (error) {
    const code = error && error.code || 'UNKNOWN';
    yield put(actions._useCaseBoardCopyFailed(accountId, boardId, copyTeam, code));
    if(isNetworkError(error) || error.code === 504){
      return;
    }
    console.error("useCaseBoardCopy > failed due to this: ", error);
  }
}

/**
 * Wait till auth data available.
 */
function waitTillOwnedAccountAvailable() {
  let resolve, reject;
  const promise = new Promise((res, rej) => { resolve = res, reject = rej; });
  const unsubscribe = ReduxUtils.subscribe(store, `auth.ownedAccounts`, (ownedAccounts)=> {
    if(!isEmpty(ownedAccounts)) {
      unsubscribe && unsubscribe();
      resolve();
    }
  });
  return promise;
}

/**
 * Navigates to the board page, removing the 'copy-board' action param if present.
 * @param {Object} state Redux state
 * @param {String|Number} accountId
 * @param {String|Number} boardId
 * @param {String} actionType
 */
function navigateToBoard(state, accountId, boardId, actionType) {
  // Remove action=copy-board so browser back goes to explore, not dialog.
  if (router.selectors.actionName(state) === 'copy-board') {
    router.actions.setQueryParams({ 'action': null });
  }
  router.actions.navigate(`/${accountId}/board/${boardId}`, actionType === 'copy-use-case-board' || actionType === 'create-board-ai');
}

/**
 * Open board and ensure required data is loaded before navigation.
 * @param {String|Number} boardId Board Id
 * @param {String|Number} accountId Account Id
 * @param {String} actionType Action type for navigation context
 * @param {Boolean} copyTeam Whether to copy team (for use-case board copy)
 */
function* openBoard(boardId, accountId, actionType = 'copy-board', copyTeam = false) {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  try {
    // Wait for board data to be available.
    const boardRequest = firestoreRedux.getDocById('boards', boardId, { once: true, waitTillSucceed: true });
    const boardAttrs = yield boardRequest.result;

    // If user is not the owner, ensure their membership data is loaded.
    if (boardAttrs && boardAttrs.ownerId !== userId) {
      const membersQuery = firestoreRedux.query('board-team-members', {
        where: [['boardId', '==', boardId], ['userId', '==', userId]],
        once: true,
        waitTillSucceed: true,
      });
      yield membersQuery.result;
    }
  } catch (error) {
    console.warn("openBoard > failed due to this: ", error);
  }

  // Dispatch appropriate done action based on context.
    if (actionType === 'copy-use-case-board') {
    yield put(actions._useCaseBoardCopyDone(accountId, boardId, copyTeam));
  } else {
    yield put({ type: actions.BOARD_COPY_DONE, accountId, boardId });
  }

  // Navigate to the board.
  navigateToBoard(state, accountId, boardId, actionType);
}

/**
 * Loads board's basic details. (Only when board is accessible to user.)
 * @param {Object} action action payload. e.g {type: BOARDS_LOAD_BASIC_DETAILS, reuesterId, boardIds: [234234, 234234]}
 */
function* loadBoardsBasicDetails(action) {
  try {
    if (!action.requesterId || !action.boardIds) {
      console.warn('loadBoardsBasicDetails: Required data is not provided.');
      return;
    }
    const state = yield select();
    const userId = auth.selectors.currentUserId(state);
    const _accessibleBoards = accessibleBoards(state);
    if (isEmpty(_accessibleBoards)) {
      console.warn('loadBoardsBasicDetails: No accessible board found.');
      return;
    }

    forEach(action.boardIds, (boardId) => {
      if (_accessibleBoards[boardId]) {
        firestoreRedux.getDocById('boards', boardId, { requesterId: action.requesterId });
        if(userId) {
          firestoreRedux.query('board-unread-user-details', { id: `board-unread-user-details-${boardId}`, where: [['boardId', '==', boardId], ['userId', '==', userId], ['anyUnread', '==', true]], requesterId: action.requesterId });
        }
      }
    })
  } catch (error) {
    console.error("loadBoardsBasicDetails > failed due to this: ", error);
  }
}

/**
 * Disconnects boards details.
 * @param {Object} action action payload. e.g {type: BOARDS_BASIC_DETAILS_DISCONNECT, requesterId}
 */
function* disconnectBoardsBasicDetails(action) {
  if (!action.requesterId) {
    console.warn('disconnectBoardsBasicDetails: Required data is not provided.');
    return;
  }
  firestoreRedux.cancelQueryByRequester(action.requesterId);
}

/**
 * Opens board.
 * @param {Object} action action palyload. e.g `{type: 'BOARD_OPEN', boardId, accountId}`
 */
function* open(action) {
  router.actions.navigate(`/${action.accountId}/board/${action.boardId}`, true);
}

/**
 * Given card already in selected-cards then remove card from selected-cards,
 * Otherwise add card into selected-cards.
 * @param {Object} action action palyload.
 */
function* toggleCardSelection(action) {
  const state = yield select();
  const selectedColumn = selectors.selectedColumn(state);
  let selectedCards = selectors.selectedCards(state);

  if(isEmpty(selectedCards) || action.columnId !== selectedColumn) {
    store.dispatch(actions.setSelection([action.cardId], action.columnId));
    return;
  }

  if(selectedCards.indexOf(action.cardId) !== -1) {
    selectedCards = without(selectedCards, action.cardId);
  } else {
    selectedCards.push(action.cardId);
  }

  store.dispatch(actions.setSelection(selectedCards, action.columnId));
}

/**
 * Card select upto given card.
 * @param {Object} action action palyload.
 */
function* uptoCardSelection(action) {
  const state = yield select();
  const selectedCards = selectors.selectedCards(state);
  const selectedColumn = selectors.selectedColumn(state);

  if(isEmpty(selectedCards) || action.columnId !== selectedColumn) {
    store.dispatch(actions.setSelection([action.cardId], action.columnId));
    return;
  }

  const cardsByColumn = map(selectors.cardsByColumn(state, selectedColumn) || [], 'id');
  let topSideLastSelectedCard;
  let bottomSideFirstSelectedCard;

  //Find top-side last selected Card.
  forEach(cardsByColumn, (cardId) => {
    if(selectedCards.indexOf(cardId) !== -1 && cardId !== action.cardId) {
      topSideLastSelectedCard = cardId;
    }

    if(cardId === action.cardId) {
      return false;
    }
  });

  //Find bottom side first selected card.
  forEachRight(cardsByColumn, (cardId) => {
    if(selectedCards.indexOf(cardId) !== -1 && cardId !== action.cardId) {
      bottomSideFirstSelectedCard = cardId;
    }

    if(cardId === action.cardId) {
      return false;
    }
  });

  //Compute new selected card based on `topSideLastSelectedCard` and `bottomSideFirstSelectedCard`.
  let newSelectedCards = selectedCards;
  if(topSideLastSelectedCard) {
    let topCards = cardsByColumn.slice(cardsByColumn.indexOf(topSideLastSelectedCard), cardsByColumn.indexOf(action.cardId));
    newSelectedCards = [...newSelectedCards, ...topCards];
  }

  if(bottomSideFirstSelectedCard) {
    let bottomCards = cardsByColumn.slice(cardsByColumn.indexOf(action.cardId), cardsByColumn.indexOf(bottomSideFirstSelectedCard));
    newSelectedCards = [...newSelectedCards, ...bottomCards];
  }

  newSelectedCards.push(action.cardId);
  store.dispatch(actions.setSelection(newSelectedCards, selectedColumn));
}

/**
 * Selects all cards in provided column.
 * @param {Object} action action payload. e.g {type: BOARD_CARD_SELECT_ALL, columnId}
 */
function* selectAllCards({ columnId }) {
  const state = yield select();
  const cards = selectors.cardsByColumn(state, columnId) || [];
  const cardIds = map(cards, 'id');
  yield put({ type: actions.CARD_SELECTION_UPDATE, cardIds, columnId });
}

/**
 * Subscribe on selectedColumn.
 */
let lastCardsByColumn;
function* subscribeSelectedColumn() {
  let unsubscribe;
  ReduxUtils.subscribe(store, `board.selectedColumn`, (selectedColumn)=> {
    unsubscribe && unsubscribe();

    if(!selectedColumn) {
      return;
    }
    lastCardsByColumn = undefined;
    unsubscribe = ReduxUtils.subscribe(store, `firestore.docs.cards`, onSelectedColumnCardsChange);
  });
}

/**
 * Subscribe live listener for board accountId changes.
 * If accountId is change for current board and is not match with router data
 *   - change new board url with replace state
 *
 * How to change the account ID of the board?
 * - When a user transfers the board to another user and the board is transferred to the account of another user,
 *   then the accountId changes.
 */
let unsubscribeAccountIdHandler;
function* subscribeAccountId() {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const routerAccountId = router.selectors.accountId(state);

  unsubscribeAccountIdHandler && unsubscribeAccountIdHandler();

  const accountIdChangeCheck = () => {
    const currentState = store.getState();
    // The page board is a retained background — a linked card is stacked over a
    // different source board — so its account legitimately differs from the
    // router (card) account. That's not a board transfer; re-navigating here
    // would swap the background out and close the stacked card.
    if (router.selectors.dialogName(currentState) === 'CARD'
        && router.selectors.pageBoardId(currentState) !== router.selectors.dialogBoardId(currentState)) {
      return;
    }
    const boardAttrs = selectors.attrs(currentState);
    const movingStatus = boardAttrs && boardAttrs.movingStatus || '';
    if(boardAttrs && get(boardAttrs, 'accountId') !== routerAccountId && movingStatus !== 'ACCEPTED' && movingStatus !== 'FAILED') {
      router.actions.navigate(`/${boardAttrs.accountId}/board/${boardAttrs.id}`, true);
      unsubscribeAccountIdHandler && unsubscribeAccountIdHandler();
    }
  }

  unsubscribeAccountIdHandler = ReduxUtils.subscribe(store, [`firestore.docs.boards.${boardId}`, `firestore.docs.board-move-requests.${`bmr_${getIdWoPrefix({id: boardId, prefix: 'brd_'})}`}`], () => {
    accountIdChangeCheck();
  });
}

/**
 * Loads user attrs when she is added into board team.
 */
let unsubscribeBoardMembersHandlers = {};
function* subscribeBoardMembers({boardId, loadAccountTeam, requesterId}) {
  try {
    let state = yield select();
    boardId = boardId || router.selectors.pageBoardId(state);
    requesterId = requesterId || `board-page-${boardId}`;
    // Loads board owner detals if not loaded.
    let boardAttrs = selectors.attrs(state, boardId);
    if (isEmpty(boardAttrs)) {
      const request = firestoreRedux.getDocById('boards', boardId, { once: true });
      boardAttrs = yield request.result;
    }
    const ownerId = boardAttrs && boardAttrs.ownerId;
    const accountId = boardAttrs && boardAttrs.accountId;
    const ownerDetails = firestoreRedux.selectors.doc(state, 'users', ownerId);
    if (ownerId && isEmpty(ownerDetails)) {
      firestoreRedux.getDocById('users', ownerId, { requesterId });
    }

    //Load board createdBy details.
    const createdBy = boardAttrs && boardAttrs.createdBy;
    const createdByDetails = firestoreRedux.selectors.doc(state, 'users', createdBy);
    if (createdBy && isEmpty(createdByDetails)) {
      firestoreRedux.getDocById('users', createdBy, { requesterId });
    }

    // Loads board account team members details.
    unsubscribeBoardMembersHandlers[boardId] && unsubscribeBoardMembersHandlers[boardId]();
    unsubscribeBoardMembersHandlers[boardId] = ReduxUtils.subscribe(store, loadAccountTeam ? 'firestore.docs.account-team-members': 'firestore.docs.board-team-members' , (members) => {
      if(loadAccountTeam) {
        members = filter(members, { accountId});
      } else {
        members = filter(members, { boardId});
      }
      if (isEmpty(members)) {
        return;
      }
      state = store.getState();
      forEach(members, (member) => {
        if (isEmpty(member) || !member.userId) {
          return;
        }
        const userDetails = firestoreRedux.selectors.doc(state, 'users', member.userId);
        if (!userDetails) {
          firestoreRedux.getDocById('users', member.userId, { requesterId });
        }
      })
    });
  } catch (error) {}
}

/**
 * Unsubscribes board team members.
 */
function* unsubscribeBoardMembers({ boardId }) {
  const state = yield select();
  boardId = boardId || router.selectors.pageBoardId(state);
  if (unsubscribeBoardMembersHandlers[boardId]) {
    unsubscribeBoardMembersHandlers[boardId]();
    delete unsubscribeBoardMembersHandlers[boardId];
  }
}

let unsubscribeAccountMembersHandlers = {};
let unsubscribeAccountOwnersHandlers = {};
function* subscribeAccountMembers({accountId, requesterId}) {
  try {
    requesterId = requesterId || `account-team-members-${accountId}`;
    unsubscribeAccountOwnersHandlers[accountId] = ReduxUtils.subscribe(store, `firestore.docs.accounts.${accountId}.ownerId`, (ownerId) => {
      if(ownerId) {
        firestoreRedux.getDocById('users', ownerId, { requesterId });
      }
    });

    // Loads board account team members details.
    const chunkSize = 10;
    unsubscribeAccountMembersHandlers[accountId] = ReduxUtils.subscribe(store, 'firestore.docs.account-team-members', async(members) => {
      members = filter(members, { accountId });
      if (isEmpty(members)) {
        return;
      }

      let state = store.getState();
      const _membersIds = [];
      forEach(members, (member) => {
        if (isEmpty(member) || !member.userId) {
          return;
        }
        const userDetails = firestoreRedux.selectors.doc(state, 'users', member.userId);
        if (!userDetails) {
          _membersIds.push(member.userId);
        }
      })

      let promises = [];
      for (let i = 0; i < _membersIds.length; i++) {
        const userId = _membersIds[i];
        try {
          const userQuery = firestoreRedux.getDocById('users', userId, { requesterId });
          promises.push(userQuery.result);
        } catch (error) {
          console.error("subscribe account members failed due to this: ", error);
        }
      }

      try {
        if (!isEmpty(promises)) {
          await Promise.all(promises);
        }
      } catch (error) {}
    });
  } catch (error) {}
}

function* unsubscribeAccountMembers({accountId}) {
  const state = yield select();
  accountId = accountId || router.selectors.accountId(state);
  if (unsubscribeAccountMembersHandlers[accountId]) {
    unsubscribeAccountMembersHandlers[accountId]();
    delete unsubscribeAccountMembersHandlers[accountId];
  }
  if (unsubscribeAccountOwnersHandlers[accountId]) {
    unsubscribeAccountOwnersHandlers[accountId]();
    delete unsubscribeAccountOwnersHandlers[accountId];
  }
}

function* onPageOpened({id}) {
  yield spawn(hideImportBoardRequestSuccessToast, id);
  yield spawn(showAIBoardCreatedSuccessToast, id);
}

function* hideImportBoardRequestSuccessToast(boardId) {
  hideSnackBar(`import-board-request-success-${boardId}`);
}

/**
 * Shows a success toast when an AI-created board or template is created by the current user.
 * - Only shows for the creator (not impersonated users).
 * - Only shows if the board was created by AI (importBoardDetail.service === 'AI').
 * - Only shows if source is 'ai-flow'.
 * - Only shows within 10 minutes of creation.
 * - Provides an undo action to delete the board/template and navigate to the home page.
 *
 * @param {String|Number} boardId - The ID of the board to check and show toast for.
 */
function* showAIBoardCreatedSuccessToast(boardId) {
  const state = yield select();
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  if (impersonatedUser) {
    return;
  }

  // Only show toast if source is 'ai-flow'
  const source = get(state, 'router.page.params.source');
  if (source !== 'ai-flow') {
    return;
  }

  try {
    // Fetch board attributes, waiting until available.
    const attrsQuery = firestoreRedux.getDocById('boards', boardId, { once: true, waitTillSucceed: true });
    const attrs = yield attrsQuery.result;
    const currentUserId = auth.selectors.currentUserId(state);

    // Only show for the creator.
    const isCreator = attrs && attrs.createdBy === currentUserId;
    if (!isCreator) {
      return;
    }

    // Only show if created by AI.
    const aiCreated = attrs?.importBoardDetail?.service?.toLowerCase() === 'ai';
    if (!aiCreated) {
      return;
    }

    analytics.logBoardCreatedEvent(boardId);

    const isTemplate = !!attrs?.template;
    const createdAt = attrs?.createdAt || 0;
    const now = Date.now();
    const createdAtMs = typeof createdAt === 'number' ? createdAt : 0;
    const elapsed = now - createdAtMs;
    const maxUndoMs = 10 * 60 * 1000; // 10 minutes

    if (createdAtMs && elapsed < maxUndoMs) {
      const remaining = Math.max(1000, maxUndoMs - elapsed);
      const accountId = attrs?.accountId || router.selectors.accountId(state);

      const undoActionButton = {
        caption: isTemplate ? i18next.t('buttons.removeTemplate') : i18next.t('buttons.removeBoard'),
        callback: () => {
          if (isTemplate) {
            analytics.logEvent('create template ai undo', { boardId });
          } else {
            analytics.logEvent('create board ai undo', { boardId });
          }
          store.dispatch({ type: boardExplorerActions.PERMANENT_DELETE, id: boardId });
          router.actions.navigate(`/${accountId}/home/favorite/boards`, true);
        }
      };
      showSnackBar({
        id: `ai-board-created-success-${boardId}`,
        message: isTemplate ? i18next.t('toast.AI.templateCreated') : i18next.t('toast.AI.boardCreated'),
        actionButton: undoActionButton,
        timeout: remaining
      });
    }
  } catch (error) {
    console.error("showAIBoardCreatedSuccessToast > failed due to this: ", error);
  }
}

function* hideAIBoardCreatedSuccessToast(boardId) {
  hideSnackBar(`ai-board-created-success-${boardId}`);
}

/**
 * Disconnects board members when board page is closed.
 */
function* onPageClosed({id}) {
  yield spawn(unsubscribeBoardData, id);
  yield spawn(hideAIBoardCreatedSuccessToast, id);
}

/**
 * Disconnects previous board data when it's changed.
 */
 function* onPageChanged({previousBoardId, id}) {
   yield spawn(unsubscribeBoardData, previousBoardId);
   yield spawn(hideImportBoardRequestSuccessToast, id);
   yield spawn(hideAIBoardCreatedSuccessToast, previousBoardId);
   yield spawn(showAIBoardCreatedSuccessToast, id);
 }

 /**
  * Unsubscribe from board team members, account id & boad page data.
  * @param {String} boardId Board Id.
  */
function* unsubscribeBoardData(boardId) {
  yield call(unsubscribeBoardMembers, { boardId });
  unsubscribeAccountIdHandler && unsubscribeAccountIdHandler();
  firestoreRedux.cancelQueryByRequester(`board-page-${boardId}`);
  firestoreRedux.cancelQueryByRequester(`board-team-${boardId}`);
  unsubscribeAiPendingEdits(boardId);
}

// ---------------------------------------------------------------------------
// AI pending edits — overlay infrastructure (M2)
// ---------------------------------------------------------------------------
//
// For each AI chat whose `pendingEditBoardIds` includes the current board, fire
// a live query of the chat's `ai-tool-messages`. Whenever the tool-message
// docs change — or a watched server doc updates — re-evaluate which WAITING
// ToolCalls should materialise on the board via localWrite.
//
// Tool variants currently wired through to localWrite:
//   - createCard           → 'cards' (full new doc) + fan-out into
//                            'tasks' / 'card-attachments' / 'card-chat-messages'
//                            for any bundled sub-entities supplied via
//                            tc.input.subtasks[] / attachments[] / chatMessages[]
//   - updateCard<Field>    → 'cards' overlay: { ...serverCard, [field]: proposed, [old<Field>]: server[field] }
//   - moveCard             → 'cards' overlay with `columnId` + `oldColumnId` (and `columnOrder` when supplied)
//   - deleteCard           → 'cards' overlay with `aiPendingDelete: true` on top of the server card
//   - createSubtask,       → 'tasks' localWrite (see the sub-task pipeline below):
//     updateSubtask*,        create writes the task; update overlays the field +
//     deleteSubtask          old<Field> snapshot; delete flags `aiPendingDelete`.
//
// The attachment + chat-message sub-entity mutations are not yet wired — they land
// in their own later tasks following the same skeleton.

// updateCard<Field> derives the card field from the tool name (updateCardTitle → title),
// and most tools name their input after that field. A few differ: updateCardAssignments
// sends `assignees` (matching updateSubtaskAssignments) for the `assignments` field.
const CARD_EDIT_INPUT_KEY = { assignments: 'assignees' };

const aiPendingEditsUnsubscribers = {}; // { boardId: Function }
const aiPendingEditsLocalCardIds = {};  // { boardId: Set<cardId> }  ← createCard overlays
const aiPendingEditsEditCardIds = {};   // { boardId: Set<cardId> }  ← updateCard*/moveCard overlays
const aiPendingEditsDeleteCardIds = {}; // { boardId: Set<cardId> }  ← deleteCard overlays
// Bundled sub-entity localWrites for a NEW card, keyed by boardId → cardId →
// { collection → Array<{ id, queryId }> }. Lets cleanup walk every bundled
// doc when the parent NEW card leaves WAITING.
const aiPendingEditsBundles = {};
// Standalone sub-task localWrites, keyed by boardId → Map<subtaskId, { queryId, type }>
// where type is 'create' | 'update'. Lets cleanup drop (create) or restore (update)
// each task localWrite when its proposal leaves WAITING.
const aiPendingEditsSubtaskIds = {};
// Standalone attachment localWrites, keyed by boardId → Map<attachmentId, { queryId, type }>.
// type 'create' | 'update' | 'delete': cleanup drops (create) or restores (update/delete).
const aiPendingEditsAttachmentIds = {};
// Standalone chat-message localWrites, keyed by boardId → Map<chatMessageId, { queryId, type }>.
// type 'create' | 'update' | 'delete': cleanup drops (create) or restores (update/delete).
const aiPendingEditsChatIds = {};
// The board-level overlays we wrote, per surface, so cleanup can roll each back.
// boardId → Map<entityId, { type: 'create' | 'update' | 'delete' | 'merge' | 'remove' }>.
const aiPendingEditsBoardTagIds = {};
const aiPendingEditsColumnIds = {};
// { boardId: Map<columnId, { original, docId }> } ← per-user visibility overlays (proposed hide/show)
const aiPendingEditsColumnVisibility = {};
const aiPendingEditsTeamIds = {};
const aiPendingEditsBoardAttachmentIds = {};
const aiPendingEditsBoardChatIds = {};
// Settings live on one board doc, so just a presence flag: { boardId: true }.
const aiPendingEditsSettingsBoardIds = {};
// Off-page chat messages already fetched by id, so each loads once: boardId → Set<chatMessageId>.
const aiPendingEditsBoardChatLoadRequested = {};
// A proposed link's preview is found in the browser (once per url) and cached; reapply
// re-runs so the overlay picks it up.
const aiAttachmentPreviewCache = {}; // url → { subType?, previewAvailable? }
const aiAttachmentPreviewPending = {}; // url → true while a lookup is in flight
const aiPendingEditsReapply = {}; // boardId → reapply()
const aiPendingEditsApplyInFlight = {}; // { boardId: Boolean }      ← re-entrance guard
const aiPendingEditsLoading = {};       // { boardId: runToken }     ← set while loadAiPendingEdits runs, before its unsubscriber exists (blocks double-spawn)
/**
 * True while `tc` is a live proposal: WAITING_FOR_APPROVAL, and not already approved or
 * rejected by this client. Mirrors `aiChat.selectors.isAwaitingApproval` — read straight
 * off the state slice, as this module deliberately doesn't import the ai-chat module (see
 * the tool-message read in `applyAiPendingEditsForBoard`).
 */
function isAwaitingApproval(state, tc) {
  return tc?.status === 'WAITING_FOR_APPROVAL' && !get(state, `ai-chat.resolvedToolCalls.${tc.id}`);
}

/** True once the user has approved `tc` — the proposed value is on its way to becoming
 *  the committed one, so the overlay is committed rather than rolled back. Reads the
 *  client's own record too, for the same reason `isAwaitingApproval` does: the server
 *  status can still say WAITING_FOR_APPROVAL for a beat after the user clicks Apply. */
function isApproved(state, tc) {
  return tc.status === 'IN_PROGRESS'
    || tc.status === 'SUCCESS'
    || get(state, `ai-chat.resolvedToolCalls.${tc.id}`) === 'APPROVED';
}

function* loadAiPendingEdits({ boardId, userId }) {
  if (!boardId || !userId) return;
  const requesterId = `board-page-${boardId}`;
  // Claim the board right away, so a second run can't start while we await below.
  // A token, not a flag, so each run can tell whether it's still the current one.
  const runToken = {};
  aiPendingEditsLoading[boardId] = runToken;

  try {
    // Live query — every chat with pending edits touching this board, whether it runs ON
    // this board or on another board (cross-board proposals). `pendingEditBoardIds` includes
    // the chat's own board, so this single query covers both; the cross-board selectors
    // narrow to `context.boardId !== boardId`. Drives the board overlay + ai-chat-fab-button's
    // [View AI Chat]. (Query id kept as `incoming-edits` to avoid churn — it now also matches
    // own-board chats.)
    const chatsQuery = firestoreRedux.query('ai-chats', {
      id: `ai-chats-incoming-edits-${boardId}`,
      where: [
        ['pendingEditBoardIds', 'array-contains', boardId],
        ['userId', '==', userId],
      ],
      requesterId,
    });

    yield chatsQuery.result;

    // Stop if this run is stale — the board was left, or a newer run took over.
    if (aiPendingEditsLoading[boardId] !== runToken) return;

    // Subscribe to chat-list / tool-message / cards changes so we (un)load
    // tool-message queries as chats enter / leave the pending state, re-run
    // apply on every tool status flip, and re-overlay AI proposals on top of
    // any concurrent server card changes. The in-flight guard breaks the
    // apply → card localWrite → cards-subscription → apply recursion.
    const reapply = () => {
      if (aiPendingEditsReapply[boardId] !== reapply) return;
      if (aiPendingEditsApplyInFlight[boardId]) return;
      aiPendingEditsApplyInFlight[boardId] = true;
      try {
        const chats = pendingEditChatsForBoard(store.getState(), boardId);
        loadToolMessage(boardId, requesterId, chats);
        loadReferencedBoards(boardId, requesterId, chats);
        loadReferencedCards(boardId, requesterId, chats);
        applyAiPendingEditsForBoard(boardId, chats);
      } finally {
        aiPendingEditsApplyInFlight[boardId] = false;
      }
    };
    // Let an attachment preview lookup re-run apply once it resolves.
    aiPendingEditsReapply[boardId] = reapply;

    const unsubscribe = ReduxUtils.subscribe(store, [
      `firestore.docs.ai-chats`,
      `firestore.docs.ai-tool-messages`,
      `firestore.docs.cards`,
      // `tasks` so an updateSubtask* overlay re-applies once a card's sub-tasks
      // load (they may load after the proposal arrived). The in-flight guard above
      // breaks the apply → task localWrite → tasks-subscription → apply recursion.
      `firestore.docs.tasks`,
      // `card-attachments` for the same reason: an updateAttachmentTitle overlay needs
      // the existing attachment doc, which loads lazily when the ATTACHMENTS tab opens
      // (after the proposal arrived). Same in-flight guard breaks the recursion.
      `firestore.docs.card-attachments`,
      // `card-chat-messages` likewise: an updateChatMessage overlay needs the existing
      // message, which loads lazily when the CHATS tab opens.
      `firestore.docs.card-chat-messages`,
      // The board-level collections, same reason — each surface's overlay re-applies on top of
      // the latest server doc (and a concurrent edit updates its old<Field> snapshot).
      `firestore.docs.board-tags`,
      `firestore.docs.columns`,
      // `hidden-columns` so a per-user visibility overlay re-applies over a concurrent server change.
      `firestore.docs.hidden-columns`,
      `firestore.docs.board-team-members`,
      // `users` so an addBoardTeamMember-by-email overlay re-applies once that user's doc loads
      // (the email → userId resolution needs it).
      `firestore.docs.users`,
      `firestore.docs.board-attachments`,
      `firestore.docs.boards`,
      `firestore.docs.board-chat-messages`,
    ], reapply);

    aiPendingEditsUnsubscribers[boardId] = unsubscribe;
  } catch (error) {
    console.error('Failed to load AI pending edits for board', boardId, error);
  } finally {
    // Only clear our own reservation — a newer run may have replaced it.
    if (aiPendingEditsLoading[boardId] === runToken) delete aiPendingEditsLoading[boardId];
  }
}

/**
 * Every pending-edit chat this board shows — those running on it and those on other
 * boards targeting it. All are overlaid so proposals from more than one chat show.
 * (The server should keep it to one chat per board; if not, we surface them all
 * instead of hiding the older ones.) The render side resolves each card to its own
 * owning chat via `pendingEditChatId`, so overlay and UI still agree.
 *
 * Also includes chats started on this board with a pending move OUT of it, so this board
 * can drop the departing card from its columns. (The incoming query misses these — a move
 * only "lands" on the destination board — so we match them by their context board.)
 */
function pendingEditChatsForBoard(state, boardId) {
  const incomingChats = firestoreRedux.selectors.docsByQueryResult(state, `ai-chats-incoming-edits-${boardId}`, 'ai-chats') || [];
  const allChats = get(state, 'firestore.docs.ai-chats') || {};
  const outgoingChats = [];
  for (const chatId in allChats) {
    const chat = allChats[chatId];
    if (chat && chat.context && chat.context.boardId === boardId
      && Array.isArray(chat.pendingEditBoardIds) && chat.pendingEditBoardIds.length
      && !incomingChats.some((existing) => existing.id === chatId)) {
      outgoingChats.push(chat);
    }
  }
  return outgoingChats.length ? [...incomingChats, ...outgoingChats] : incomingChats;
}

/**
 * Loads the board docs referenced by this board's pending ToolCalls (`input.boardId`), so
 * ai-chat-board-group-header can resolve board names without a per-render fetch. Runs from
 * the reapply subscription, so it re-runs once tool messages arrive; skips already-loaded boards.
 */
function loadReferencedBoards(boardId, requesterId, chats) {
  const state = store.getState();
  const boardIds = new Set();
  for (const chat of chats) {
    const toolMessages = firestoreRedux.selectors.docsByQueryResult(state, `ai-tool-messages-${chat.id}`, 'ai-tool-messages') || [];
    for (const tm of toolMessages) {
      for (const tc of tm.toolCalls || []) {
        if (tc.input?.boardId) boardIds.add(tc.input.boardId);
      }
    }
  }
  for (const bId of boardIds) {
    if (isEmpty(selectors.attrs(state, bId))) firestoreRedux.getDocById('boards', bId, { requesterId });
  }
}

/**
 * Loads the existing card docs referenced by this board's pending edit / delete ToolCalls
 * (`input.cardId`), so the AI panel can resolve titles / columns for cards on OTHER boards
 * (cross-board proposals) without the user first visiting those boards. `createCard` cards
 * are materialised via localWrite (not on the server), so they're skipped. Runs from the
 * reapply subscription; skips already-loaded cards.
 */
function loadReferencedCards(boardId, requesterId, chats) {
  const state = store.getState();
  const byCardId = new Map(); // cardId → { boardId, shouldFetchOnce }
  for (const chat of chats) {
    const toolMessages = firestoreRedux.selectors.docsByQueryResult(state, `ai-tool-messages-${chat.id}`, 'ai-tool-messages') || [];
    for (const tm of toolMessages) {
      for (const tc of tm.toolCalls || []) {
        if (tc.name === 'createCard') continue; // materialised via localWrite, not fetched
        if (!tc.input?.cardId) continue;
        // The board the card is loaded from. A moveCard's `input.boardId` is the DESTINATION,
        // but the card still lives on its source board (the chat's context board) until
        // applied — load it from there, and just once: a live source listener would fight the
        // move overlay over its `boardId`. Other edits act on the card in place, so
        // `input.boardId` is its board.
        const sourceBoardId = tc.name === 'moveCard'
          ? (chat?.context?.boardId || tc.input.boardId)
          : tc.input.boardId;
        if (sourceBoardId) byCardId.set(tc.input.cardId, { boardId: sourceBoardId, shouldFetchOnce: tc.name === 'moveCard' });
      }
    }
  }

  for (const [cardId, { boardId: sourceBoardId, shouldFetchOnce }] of byCardId) {
    // Cards live in the `boards/{boardId}/cards` subcollection — load the single doc by id
    // there (not the `cards` collection group); it normalises into `firestore.docs.cards` for
    // the panel. Skip cards already in state.
    if (!firestoreRedux.selectors.doc(state, 'cards', cardId)) {
      firestoreRedux.getDocById(`boards/${sourceBoardId}/cards`, cardId, { requesterId, once: shouldFetchOnce });
    }
  }
}

function loadToolMessage(boardId, requesterId, chats) {
  const state = store.getState();
  for (const chat of chats) {
    if (!chat?.id) continue;
    // Always register under the board's requesterId, even when a query for this
    // chat is already firing (e.g. the AI panel's). The panel's query stops when
    // the panel closes, so the board needs its own requester to keep the
    // tool-message subscription alive. Re-registering the same (id, requesterId)
    // is idempotent.
    firestoreRedux.query('ai-tool-messages', {
      id: `ai-tool-messages-${chat.id}`,
      where: [['chatId', '==', chat.id]],
      orderBy: [['createdAt', 'asc']],
      requesterId,
    });
  }
}

/**
 * Re-applies the localWrite overlays for the given board based on the current
 * state of every pending-edit chat's tool messages. Idempotent — safe to call
 * on every subscription tick.
 */
function applyAiPendingEditsForBoard(boardId, chats) {
  const state = store.getState();

  const cardsQueryId = `cards_${boardId}`;
  const previouslyNew = aiPendingEditsLocalCardIds[boardId] || new Set();
  const previouslyEdited = aiPendingEditsEditCardIds[boardId] || new Set();
  const previouslyDeleted = aiPendingEditsDeleteCardIds[boardId] || new Set();
  const stillNew = new Set();
  const deleteCardIds = new Set(); // cardIds with a WAITING deleteCard ToolCall
  // EDIT proposals grouped by cardId — multiple updateCard* calls on the
  const editProposals = new Map(); // cardId → { proposed: {...}, oldFieldNames: Set<string> }
  // How each card field's most recent proposal was resolved, for the cleanup pass below.
  const resolvedEdits = new Map(); // cardId → Map<field, ToolCall>
  const toolCalls = []; // every chat's tool calls, flattened for the sub-task pipeline

  for (const chat of chats) {
    const chatId = chat?.id;
    if (!chatId) continue;
    // Read tool-message docs directly via firestoreRedux to avoid importing
    const toolMessages = firestoreRedux.selectors.docsByQueryResult(
      state,
      `ai-tool-messages-${chatId}`,
      'ai-tool-messages'
    ) || [];
    for (const tm of toolMessages) {
      if (!Array.isArray(tm.toolCalls)) continue;
      toolCalls.push(...tm.toolCalls);
      for (const tc of tm.toolCalls) {
        const cardId = tc.input?.cardId;
        if (!isAwaitingApproval(state, tc)) {
          // Not a live proposal any more — remember how it was resolved, so the cleanup
          // pass below can tell an approved overlay (commit the proposed value) from a
          // rejected one (roll back to the `old<Field>` snapshot).
          if (cardId) noteResolvedEditProposal(resolvedEdits, cardId, tc);
          continue;
        }
        if (!cardId) continue;

        // plus bundled sub-entity fan-out for any subtasks / attachments /
        if (tc.name === 'createCard') {
          if (tc.input?.boardId !== boardId) continue;
          stillNew.add(cardId);
          const columnId = tc.input.columnId;
          const columnType = columns.column(state, columnId)?.type || 'OTHER';
          firestoreRedux.save('cards', {
            id: cardId,
            boardId,
            columnId,
            columnType,
            title: tc.input.title || '',
            status: tc.input.status || 'READY',
            description: tc.input.description || '',
            // The model writes Markdown; the server turns it into HTML only once the card is accepted.
            descriptionHtml: markdownToHtml(tc.input.description),
            dueDate: toolDueTime(state, boardId, tc.input.dueDate),
            priority: tc.input.priority || 'NORMAL',
            assignments: Array.isArray(tc.input.assignees) ? tc.input.assignees : [],
            tags: Array.isArray(tc.input.tags) ? tc.input.tags : [],
            createdAt: tc.invokedAt || Date.now(),
            columnOrder: tc.input.columnOrder,
          }, { localWrite: true, queryId: cardsQueryId });

          // localWrites surface in the right TASKS / ATTACHMENTS / CHATS tab.
          writeCreateCardBundle(boardId, cardId, tc);
          continue;
        }

        // moveCard — change `columnId` (+ optional `columnOrder`), snapshot
        // the server's previous columnId so the user can see what's moving.
        if (tc.name === 'moveCard') {
          const proposed = { columnId: tc.input.columnId, columnOrder: tc.input.columnOrder };
          const oldFieldNames = ['columnId'];
          // For a cross-board move, also overlay the card's `boardId` so it reads as living
          // on the destination board — same as once the move is applied. (`oldBoardId` holds
          // the true source across re-applies, once the overlay has flipped `boardId`.)
          const existingCard = firestoreRedux.selectors.doc(state, 'cards', cardId);
          const sourceBoardId = existingCard?.oldBoardId ?? existingCard?.boardId;
          if (tc.input?.boardId && sourceBoardId && tc.input.boardId !== sourceBoardId) {
            proposed.boardId = tc.input.boardId;
            oldFieldNames.push('boardId');
          }
          collectEditProposal(editProposals, cardId, proposed, oldFieldNames);
          continue;
        }

        // updateCard<Field> — derive the field name (e.g. updateCardTitle →
        // `title`) and overlay the proposed value with a matching
        // `old<Field>` snapshot.
        if (typeof tc.name === 'string' && tc.name.startsWith('updateCard')) {
          const fieldName = resolveCardEditField(tc);
          if (!fieldName) continue;
          // The proposed value's input key usually matches the field, but not always
          // (updateCardAssignments → `assignees`); resolve it via CARD_EDIT_INPUT_KEY.
          const inputKey = CARD_EDIT_INPUT_KEY[fieldName] || fieldName;
          // The user's correction over the AI's input, as the server runs it.
          const input = tc.input && { ...tc.input, ...tc.editedInput };
          if (!input || !(inputKey in input)) continue;
          // dueDate arrives as a date or date-time string; the card model stores a timestamp.
          const proposedValue = fieldName === 'dueDate'
            ? toolDueTime(state, boardId, input[inputKey])
            : input[inputKey];
          collectEditProposal(editProposals, cardId, { [fieldName]: proposedValue }, [fieldName]);
          continue;
        }

        // deleteCard — mark the server card with an `aiPendingDelete` flag.
        // The card body / detail dialog can read this to render delete-specific
        // affordances (strike-through, "will be moved to TRASH" subtitle, etc.).
        // The card stays in its current column until the proposal is approved.
        if (tc.name === 'deleteCard') {
          deleteCardIds.add(cardId);
          continue;
        }

        // Sub-tasks are handled by the isolated pipeline below
        // (buildSubtaskChangeSet → reconcileSubtaskOverlays); attachment +
        // chat-message sub-entity mutations land in their own later tasks.
      }
    }
  }

  // Apply EDIT proposals as an overlay on top of the latest server card doc.
  // Skipped silently if the server card isn't loaded yet — the cards
  // subscription will retry once it lands.
  const stillEdited = new Set();
  for (const [cardId, { proposed, oldFieldNames }] of editProposals) {
    const serverCard = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (!serverCard) continue;
    if (stillNew.has(cardId)) continue; // createCard overlay already supplies the proposal
    stillEdited.add(cardId);

    // `serverCard` may already carry a previous tick's overlay for this card
    // (its own old<Field> + proposed value), since we only ever strip it via
    // `stripAiPendingOverlay` once every field's proposal has resolved. Any
    // field that was overlaid before but has since resolved out of this
    // tick's `oldFieldNames` (a sibling field on the same card is still
    // WAITING) must be reverted here — otherwise its stale proposed value
    // rides along in the `...overlay` spread below forever.
    const overlay = { ...serverCard };
    for (const field of overlay.aiPendingFields || []) {
      if (oldFieldNames.has(field)) continue;
      const oldKey = oldFieldKey(field);
      if (oldKey in overlay) overlay[field] = overlay[oldKey];
      delete overlay[oldKey];
    }

    const oldSnapshot = {};
    for (const field of oldFieldNames) {
      const oldKey = oldFieldKey(field);
      // Prefer the doc's own already-captured old<Field> over `overlay[field]`
      // directly — once overlaid, `overlay[field]` IS the proposed value, so
      // re-reading it as "old" on a later tick would silently adopt the
      // proposed value as the new baseline instead of the true pristine one.
      const currentValue = field === 'description' ? cardDescriptionEditorValue(overlay) : overlay[field];
      oldSnapshot[oldKey] = oldKey in overlay ? overlay[oldKey] : currentValue;
    }
    firestoreRedux.save('cards', {
      ...overlay,
      ...proposed,
      ...oldSnapshot,
      aiPendingFields: [...oldFieldNames],
    }, { localWrite: true, queryId: cardsQueryId });
  }

  // Apply DELETE overlays — stamp `aiPendingDelete: true` on top of whatever
  // the latest overlay is (server doc, EDIT overlay, or NEW overlay). Done
  // last so it stacks on top of any EDIT overlay we just wrote.
  const stillDeleted = new Set();
  for (const cardId of deleteCardIds) {
    // Read fresh from state in case the EDIT pass above just wrote an overlay
    // we want to stack on top of.
    const baseCard = firestoreRedux.selectors.doc(store.getState(), 'cards', cardId);
    if (!baseCard) continue;
    stillDeleted.add(cardId);
    firestoreRedux.save('cards', {
      ...baseCard,
      aiPendingDelete: true,
    }, { localWrite: true, queryId: cardsQueryId });
  }

  // Cleanup pass 1 — NEW cards no longer pending. Only delete the localWrite where nothing
  // else will: a rejected / cancelled / failed create has no server card coming, so its
  // fake doc must be dropped or it lingers as a ghost. An approved create is left alone —
  // firestore-redux replaces its localWrite when the server's committed card lands, and
  // deleting it here first would blank the doc for a beat (the loader flash). An in-progress
  // one is re-tracked so a later fail still drops it; a succeeded one needs no more cleanup.
  const approvedNewCards = collectApprovedCreates(state, toolCalls, 'createCard', (tc) => tc.input?.cardId);
  for (const cardId of previouslyNew) {
    if (stillNew.has(cardId)) continue;
    const createStatus = approvedNewCards.get(cardId);
    if (createStatus === 'IN_PROGRESS') { stillNew.add(cardId); continue; }
    if (createStatus === 'SUCCESS') continue;
    firestoreRedux.delete('cards', cardId, { localWrite: true, queryId: cardsQueryId });
    deleteCreateCardBundle(boardId, cardId);
  }
  // Cleanup pass 2 — EDIT cards no longer pending: strip the overlay so our
  // `old<Field>` snapshot fields disappear. A rejected field reverts to its own
  // old<Field> value; an approved one keeps the proposed value it is already
  // showing, until the server's committed snapshot lands on top. Re-saving the doc
  // unchanged here would be a no-op — `stripAiPendingOverlay` is what actually removes
  // the bookkeeping fields, exactly like the sub-task/attachment/chat pipelines.
  for (const cardId of previouslyEdited) {
    if (stillEdited.has(cardId)) continue;
    const currentCard = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (currentCard) {
      firestoreRedux.save('cards', stripAiPendingOverlay(currentCard, collectApprovedEditFields(state, resolvedEdits, cardId)), { localWrite: true, queryId: cardsQueryId });
    }
  }
  // Cleanup pass 3 — DELETE cards no longer pending: strip the `aiPendingDelete`
  // flag (and any still-lingering EDIT overlay fields) the same way. If the
  // user approved the delete, the server card itself is gone (moved to TRASH
  // column) — the doc lookup returns nothing and there's nothing to restore.
  for (const cardId of previouslyDeleted) {
    if (stillDeleted.has(cardId)) continue;
    const currentCard = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (currentCard) {
      firestoreRedux.save('cards', stripAiPendingOverlay(currentCard), { localWrite: true, queryId: cardsQueryId });
    }
  }

  // Sub-tasks (handled separately from cards): build the change set from the tool
  // calls gathered in the loop above, then write the preview overlays.
  const subtaskOps = buildSubtaskChangeSet(state, toolCalls, boardId);
  applySubtaskPreviewModel(state, boardId, subtaskOps, toolCalls);

  // Attachments (create + title change) — same shape: localWrite overlays, rolled back
  // when the proposal leaves WAITING.
  applyAttachmentPreviewModel(state, boardId, toolCalls);

  // Chat messages (create + edit) — localWrite overlays, rolled back when the proposal
  // leaves WAITING.
  applyChatPreviewModel(state, boardId, toolCalls);

  // The six board-level surfaces — same overlay contract as the card ones above.
  applyBoardTagPreviewModel(state, boardId, toolCalls);
  applyBoardColumnPreviewModel(state, boardId, toolCalls);
  applyBoardTeamPreviewModel(state, boardId, toolCalls);
  applyBoardAttachmentPreviewModel(state, boardId, toolCalls);
  applyBoardSettingPreviewModel(state, boardId, toolCalls);
  applyBoardChatPreviewModel(state, boardId, toolCalls);

  // `stillNew` already carries in-progress approved creates (re-added in cleanup pass 1) so
  // a later fail still drops them; succeeded ones are left out (firestore-redux has the card).
  aiPendingEditsLocalCardIds[boardId] = stillNew;
  aiPendingEditsEditCardIds[boardId] = stillEdited;
  aiPendingEditsDeleteCardIds[boardId] = stillDeleted;
}

// ─── Sub-task AI pending-edit pipeline ──────────────────────────────────────────
// What each sub-task tool changes:
//  - changeType: create / update / delete.
//  - field:      the task field an update changes.
//  - input:      the tool-input key holding the new value (names differ:
//                assignees → assignments, markedDone → closed).
const SUBTASK_TOOLS = {
  createSubtask: { changeType: 'create' },
  deleteSubtask: { changeType: 'delete' },
  updateSubtaskTitle: { changeType: 'update', field: 'title', input: 'title' },
  updateSubtaskAssignments: { changeType: 'update', field: 'assignments', input: 'assignees' },
  updateSubtaskDueDate: { changeType: 'update', field: 'dueDate', input: 'dueDate' },
  updateSubtaskDone: { changeType: 'update', field: 'closed', input: 'markedDone' },
  updateSubtaskOrder: { changeType: 'update', field: 'order', input: 'order' },
};

/** `old<Field>` snapshot key for an update tool's `field` (e.g. title → oldTitle). */
const oldFieldKey = (field) => `old${field.charAt(0).toUpperCase()}${field.slice(1)}`;

// The "before" side of a preview is what the card and the chat show, as the editor loads it.
const cardDescriptionEditorValue = (card) => toEditorValue({ descriptionHtml: card.descriptionHtml, description: card.description }, { isOurUrl: router.actions.isAppUrl, oldHtml: card.description2 });
const chatMessageEditorValue = (doc) => toEditorValue({ descriptionHtml: doc.messageHtml, description: doc.message }, { isOurUrl: router.actions.isAppUrl, oldHtml: doc.htmlMessage });

/**
 * The card field an edit tool changes — `updateCardTitle` → `title`, `moveCard` →
 * `columnId` — or null when the tool doesn't edit a card field. Shared by the overlay
 * pass and the resolution bookkeeping so both name the field the same way.
 */
function resolveCardEditField(tc) {
  if (tc.name === 'moveCard') return 'columnId';
  if (typeof tc.name !== 'string' || !tc.name.startsWith('updateCard')) return null;
  const suffix = tc.name.slice('updateCard'.length);
  if (!suffix) return null;
  return suffix.charAt(0).toLowerCase() + suffix.slice(1);
}

/** An AI proposal's Markdown as the editor's HTML: its `@usr_` tokens and Kerika links become mention and reference anchors. */
const proposalEditorHtml = (markdown) =>
  toEditorValue({ description: markdownToHtml(markdown) }, { isOurUrl: router.actions.isAppUrl, titleOf: titleOf(store.getState()) });

// A field's HTML twin, written by the new editor.
const HTML_TWIN = { description: 'descriptionHtml', message: 'messageHtml' };

/**
 * Takes an AI-preview overlay back off a doc — each changed field restored from its
 * `old<Field>` snapshot, and all AI markers dropped. Used by cleanup so a removed or
 * rejected update doesn't leave `aiPendingChangeType` (or proposed values) stuck on the
 * doc.
 *
 * `approvedFields` names the fields the user has approved: those keep the proposed value
 * instead of being restored. The approval only localWrites the tool-call status, so this
 * runs a beat before the server's committed doc arrives — restoring them would put the
 * old value back on screen for that beat, and the flash back and forth is what a card's
 * description does on Apply.
 */
function stripAiPendingOverlay(doc, approvedFields) {
  const clean = { ...doc };
  for (const field of clean.aiPendingFields || []) {
    const oldKey = oldFieldKey(field);
    if (oldKey in clean && !approvedFields?.has(field)) clean[field] = clean[oldKey];
    // The HTML twin is still the old value: it becomes the approved proposal's, or its Markdown reads as plain text.
    if (approvedFields?.has(field) && HTML_TWIN[field]) clean[HTML_TWIN[field]] = proposalEditorHtml(clean[field]);
    delete clean[oldKey];
  }
  delete clean.aiPendingChangeType;
  delete clean.aiPendingFields;
  delete clean.aiPendingDelete;
  delete clean.aiPendingMergeInto;
  return clean;
}

/** `{ message }` once the user has approved the chat message's update, else null: see `stripAiPendingOverlay`. */
function approvedMessageFields(state, toolCalls, updateToolName, chatMessageId) {
  const isUpdateApproved = (toolCalls || []).some((tc) => tc.name === updateToolName && tc.input?.chatMessageId === chatMessageId && isApproved(state, tc));
  return isUpdateApproved ? new Set(['message']) : null;
}

/**
 * Maps each `create*` overlay still on its way to a committed server doc to its progress:
 * 'IN_PROGRESS' (keep the localWrite, tracked so a later fail drops it) or 'SUCCESS' (keep
 * it, no more cleanup needed). A reject / cancel / fail is left out, so the cleanup deletes
 * its fake overlay. Status-based on purpose, NOT `isApproved`: a create that fails *after*
 * the user approved it still reads APPROVED, yet its overlay must go. `idOf` reads the
 * overlay's id from a create tool's input.
 */
function collectApprovedCreates(state, toolCalls, createToolName, idOf) {
  const statuses = new Map();
  for (const tc of toolCalls || []) {
    if (tc.name !== createToolName) continue;
    const id = idOf(tc);
    if (!id) continue;
    if (tc.status === 'SUCCESS') statuses.set(id, 'SUCCESS');
    else if (tc.status === 'IN_PROGRESS'
      || (tc.status === 'WAITING_FOR_APPROVAL' && get(state, `ai-chat.resolvedToolCalls.${tc.id}`) === 'APPROVED')) {
      statuses.set(id, 'IN_PROGRESS');
    }
  }
  return statuses;
}

/**
 * Reads the pending sub-task tool calls into a change set — all tool/field parsing
 * happens here. Several updateSubtask* calls on one sub-task merge into one `updates`
 * entry, so its diff row shows every change at once.
 *
 * A create without an explicit `order` gets the order the server will actually give it on
 * approval — see `nextAppendOrder` below.
 *
 * @param {Object} state - Redux state
 * @param {Array} toolCalls - all pending-edit tool calls (may include non-sub-task ones)
 * @param {String} boardId
 * @returns {{ creates: Map, updates: Map, deletes: Map }} each keyed by subtaskId:
 *   creates → { tasksQueryId, doc }, updates → { tasksQueryId, proposed, oldFieldNames },
 *   deletes → { tasksQueryId }
 */
function buildSubtaskChangeSet(state, toolCalls, boardId) {
  const creates = new Map();
  const updates = new Map();
  const deletes = new Map();

  // Where a create lands when the model sends no `order` — the normal case, since the tool
  // tells it to omit the field to append. task-server's TaskOrderFinder gives such a sub-task
  // `max(order of the card's OPEN sub-tasks) + 100`, or 100 on an empty checklist, so it always
  // arrives at the bottom. The preview has to say the same thing, or the row lands somewhere on
  // approval other than where the user was shown it.
  //
  // Per card, because one proposal can add sub-tasks to several cards; stepped by 100 per create,
  // because the server recomputes that max for each one in turn, so several new sub-tasks keep the
  // order the model proposed them in rather than piling onto one value. Previews from an earlier
  // run of this same pipeline are excluded: they are local-only docs, and counting them would walk
  // the order further down on every re-run.
  const allTasks = firestoreRedux.selectors.collection(state, 'tasks') || {};
  const appendOrderByCard = new Map();
  const nextAppendOrder = (cardId) => {
    if (!appendOrderByCard.has(cardId)) {
      let maxOrder = 0;
      forEach(allTasks, (task) => {
        if (!task || task.cardId !== cardId || task.closed) return;
        if (task.aiPendingChangeType === 'create') return;
        maxOrder = Math.max(maxOrder, get(task, 'order', 0));
      });
      appendOrderByCard.set(cardId, maxOrder + 100);
    }
    const order = appendOrderByCard.get(cardId);
    appendOrderByCard.set(cardId, order + 100);
    return order;
  };

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    const cardId = tc.input?.cardId;
    if (!cardId) continue;
    const subtaskTool = SUBTASK_TOOLS[tc.name];
    if (!subtaskTool) continue;
    const subtaskId = tc.input?.subtaskId;
    if (!subtaskId) continue;
    const tasksQueryId = `tasks-${boardId}-${cardId}`;

    if (subtaskTool.changeType === 'create') {
      creates.set(subtaskId, {
        tasksQueryId,
        doc: {
          id: subtaskId,
          cardId,
          boardId,
          title: tc.input.title || '',
          order: tc.input.order != null ? tc.input.order : nextAppendOrder(cardId),
          closed: !!tc.input.markedDone,
          dueDate: toolDueTime(state, boardId, tc.input.dueDate),
          assignments: Array.isArray(tc.input.assignees) ? tc.input.assignees : [],
          createdAt: tc.invokedAt || Date.now(),
          aiPendingChangeType: 'create',
        },
      });
    } else if (subtaskTool.changeType === 'delete') {
      deletes.set(subtaskId, { tasksQueryId });
    } else if (subtaskTool.changeType === 'update' && subtaskTool.input in tc.input) {
      const { field, input } = subtaskTool;
      const entry = updates.get(subtaskId) || { tasksQueryId, proposed: {}, oldFieldNames: new Set() };
      // dueDate arrives as a date or date-time string; sub-tasks store a timestamp.
      entry.proposed[field] = field === 'dueDate' ? toolDueTime(state, boardId, tc.input[input]) : tc.input[input];
      entry.oldFieldNames.add(field);
      updates.set(subtaskId, entry);
    }
  }

  return { creates, updates, deletes };
}

/**
 * Writes the sub-task preview overlays from the change set, and clears any that are
 * no longer pending. Tracks this run's overlays in aiPendingEditsSubtaskIds[boardId]
 * so the next run can spot stale ones. No tool parsing here — just applies the model.
 *
 *  - creates: save the proposed task.
 *  - updates: one overlay per sub-task with all changed fields, their old<Field>
 *             snapshot (kept once set, so it survives re-runs) and aiPendingFields.
 *             Skipped if a create already wrote the task.
 *  - deletes: flag the task with aiPendingDelete (read fresh so it stacks on an update).
 *  - cleanup: for sub-tasks no longer pending, drop the create or restore the server
 *             doc — either way the diff row disappears.
 *
 * @param {Object} state - Redux state from the start of the run.
 * @param {String} boardId
 * @param {{ creates: Map, updates: Map, deletes: Map }} changeSet - from buildSubtaskChangeSet
 */
function applySubtaskPreviewModel(state, boardId, { creates, updates, deletes }, toolCalls) {
  const previouslySubtasks = aiPendingEditsSubtaskIds[boardId] || new Map();
  const stillSubtasks = new Map(); // subtaskId → { queryId, type: 'create' | 'update' | 'delete' }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createSubtask', (tc) => tc.input?.subtaskId);

  // Grab the whole tasks hash once. `doc(state, 'tasks', id)` is just `tasks[id]`,
  // so indexing this replaces a per-sub-task selector call in the updates + cleanup
  // loops with a plain lookup.
  const tasks = firestoreRedux.selectors.collection(state, 'tasks') || {};

  for (const [subtaskId, { tasksQueryId, doc }] of creates) {
    stillSubtasks.set(subtaskId, { queryId: tasksQueryId, type: 'create' });
    firestoreRedux.save('tasks', doc, { localWrite: true, queryId: tasksQueryId });
  }

  for (const [subtaskId, { tasksQueryId, proposed, oldFieldNames }] of updates) {
    if (stillSubtasks.has(subtaskId)) continue; // a create for this id already wrote it
    const serverTask = tasks[subtaskId];
    if (!serverTask) continue;
    stillSubtasks.set(subtaskId, { queryId: tasksQueryId, type: 'update' });
    const oldSnapshot = {};
    for (const field of oldFieldNames) {
      const oldKey = oldFieldKey(field);
      oldSnapshot[oldKey] = serverTask[oldKey] !== undefined ? serverTask[oldKey] : serverTask[field];
    }
    firestoreRedux.save('tasks', {
      ...serverTask,
      ...proposed,
      ...oldSnapshot,
      aiPendingChangeType: 'update',
      aiPendingFields: [...oldFieldNames],
    }, { localWrite: true, queryId: tasksQueryId });
  }

  // Deletes stamp on top of the latest overlay, so they read the state AFTER the
  // create/update saves above — one fresh read here, not one per delete.
  const tasksAfterChanges = deletes.size ? (firestoreRedux.selectors.collection(store.getState(), 'tasks') || {}) : {};
  for (const [subtaskId, { tasksQueryId }] of deletes) {
    const baseTask = tasksAfterChanges[subtaskId];
    if (!baseTask) continue;
    stillSubtasks.set(subtaskId, { queryId: tasksQueryId, type: 'delete' });
    firestoreRedux.save('tasks', {
      ...baseTask,
      aiPendingDelete: true,
      aiPendingChangeType: 'delete',
    }, { localWrite: true, queryId: tasksQueryId });
  }

  // Clean up overlays no longer pending, in one pass. A 'create' preview is a
  // local-only doc, so it's removed outright. An 'update' or 'delete' overlay
  // sits on top of a real, still-existing task, so it's rolled back by
  // stripping the overlay (re-saving it as-is would leave aiPendingChangeType +
  // proposed values — or aiPendingDelete — stuck, e.g. on reject, where the
  // server never re-syncs a clean doc) rather than deleting the real task.
  for (const [subtaskId, { queryId, type }] of previouslySubtasks) {
    if (stillSubtasks.has(subtaskId)) continue;
    if (type === 'create') {
      // Approved → firestore-redux swaps the server task in over the localWrite, so leave
      // it; keep an in-progress one tracked so a later fail still drops it. Only a
      // reject/cancel/fail deletes (success falls through — the server task has taken over).
      const createStatus = approvedCreates.get(subtaskId);
      if (createStatus === 'IN_PROGRESS') stillSubtasks.set(subtaskId, { queryId, type: 'create' });
      else if (!createStatus) firestoreRedux.delete('tasks', subtaskId, { localWrite: true, queryId });
    } else {
      const currentTask = tasks[subtaskId];
      if (currentTask) firestoreRedux.save('tasks', stripAiPendingOverlay(currentTask), { localWrite: true, queryId });
    }
  }

  aiPendingEditsSubtaskIds[boardId] = stillSubtasks;
}

// ─── Attachment AI pending-edit pipeline (createAttachment only) ─────────────────
// The tool sends a simple type (LINK / GOOGLE_DOC / SHEET / SLIDE) and no subType.
// Map it to the card-attachments type/subType. A plain LINK's real subType (video /
// image) is found in the browser below.
const ATTACHMENT_TYPE_MAP = {
  LINK: { type: 'EXTERNAL_LINK', subType: 'WEB' },
  GOOGLE_DOC: { type: 'FILE', subType: 'DOC' },
  SHEET: { type: 'FILE', subType: 'SPREADSHEET' },
  SLIDE: { type: 'FILE', subType: 'PRESENTATION' },
};

/** iframely result → the subType/previewAvailable that give a preview. A video shows
 *  the embedly poster; an image uses the url as the thumbnail; anything else has none. */
function deriveAttachmentPreview(info) {
  switch (info?.type) {
    case 'weblink/embedly+video':
      return { subType: 'VIDEO', previewAvailable: true };
    case 'weblink/embedly+image':
      return { subType: 'IMAGE', previewAvailable: true };
    default:
      return {};
  }
}

/** Finds a link's subType/preview in the browser, once, and caches it (then re-runs
 *  apply so the overlay updates). A known file extension (pdf, mp3, docx…) is resolved
 *  right away; otherwise it loads the url as an image, then asks iframely for a video.
 *  Anything else stays a plain link. */
function resolveAttachmentPreview(boardId, url) {
  if (!url) return {};
  if (aiAttachmentPreviewCache[url]) return aiAttachmentPreviewCache[url];
  // Known file extension → subType now (sync). Only images carry a thumbnail.
  const extSubType = urlSubType(url);
  if (extSubType) {
    return (aiAttachmentPreviewCache[url] = { subType: extSubType, previewAvailable: extSubType === 'IMAGE' });
  }
  if (!aiAttachmentPreviewPending[url]) {
    aiAttachmentPreviewPending[url] = true;
    const finish = (result) => {
      aiAttachmentPreviewCache[url] = result || {};
      delete aiAttachmentPreviewPending[url];
      aiPendingEditsReapply[boardId] && aiPendingEditsReapply[boardId]();
    };
    const img = new Image();
    const timer = setTimeout(() => {
      img.onload = img.onerror = null;
      finish({});
    }, 8000);
    img.onload = () => {
      clearTimeout(timer);
      finish({ subType: 'IMAGE', previewAvailable: true });
    };
    img.onerror = () => {
      clearTimeout(timer);
      getInfo(url)
        .then((info) => finish(deriveAttachmentPreview(info)))
        .catch(() => finish({}));
    };
    img.src = url;
  }
  return {};
}

/**
 * Writes + cleans up the attachment preview overlays: createAttachment writes a new
 * card-attachments doc; updateAttachmentTitle overlays the proposed title (with an
 * oldTitle snapshot + aiPendingFields) onto the existing one. Overlays no longer pending
 * are rolled back — a create is dropped, an update is restored to the underlying doc.
 *
 * @param {Object} state - Redux state from the start of the run.
 * @param {String} boardId
 * @param {Array} toolCalls - every pending-edit chat's tool calls (flattened).
 */
function applyAttachmentPreviewModel(state, boardId, toolCalls) {
  const previously = aiPendingEditsAttachmentIds[boardId] || new Map();
  const still = new Map(); // attachmentId → { queryId, type: 'create' | 'update' }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createAttachment', (tc) => tc.input?.attachmentId);
  const attachments = firestoreRedux.selectors.collection(state, 'card-attachments') || {};
  // Proposed NEW attachments sort to the TOP: committed ones use order = -Date.now() (a
  // big negative), and the list is `sortBy(order).reverse()` (descending), so small
  // negatives sit above them. Each create takes the next one down, keeping proposal order.
  let newAttachmentOrder = -100;

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    const cardId = tc.input?.cardId;
    const attachmentId = tc.input?.attachmentId;
    if (!cardId || !attachmentId) continue;
    const queryId = `card-attachments-${cardId}`;

    if (tc.name === 'createAttachment') {
      const base = ATTACHMENT_TYPE_MAP[tc.input.type] || { type: 'EXTERNAL_LINK', subType: 'WEB' };
      // Only a plain link needs its subType found; docs already know theirs.
      const preview = base.subType === 'WEB' ? resolveAttachmentPreview(boardId, tc.input.url) : {};
      still.set(attachmentId, { queryId, type: 'create' });
      firestoreRedux.save('card-attachments', {
        id: attachmentId,
        cardId,
        boardId,
        // A null title means the server resolves it from the URL on approval; until then show the
        // URL so the preview row isn't blank.
        title: tc.input.title || tc.input.url || '',
        url: tc.input.url || '',
        type: base.type,
        subType: preview.subType || base.subType,
        previewAvailable: !!preview.previewAvailable,
        order: newAttachmentOrder--,
        createdAt: tc.invokedAt || Date.now(),
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateAttachmentTitle' && 'title' in tc.input) {
      if (still.get(attachmentId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = attachments[attachmentId];
      if (!serverDoc) continue;
      still.set(attachmentId, { queryId, type: 'update' });
      // Keep the oldTitle snapshot once set so it survives re-runs — on a re-run the doc
      // here is already the overlay, whose `title` is the proposed one.
      const oldTitle = serverDoc.oldTitle !== undefined ? serverDoc.oldTitle : serverDoc.title;
      firestoreRedux.save('card-attachments', {
        ...serverDoc,
        title: tc.input.title || '',
        oldTitle,
        aiPendingChangeType: 'update',
        aiPendingFields: ['title'],
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateAttachmentContent') {
      if (still.get(attachmentId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = attachments[attachmentId];
      if (!serverDoc) continue;
      still.set(attachmentId, { queryId, type: 'update' });
      // Content-only change: the attachment looks the same, so just mark it as an edit.
      // The diff reads oldUrl (the "Previous Version" link) off the ToolCall.
      firestoreRedux.save('card-attachments', {
        ...serverDoc,
        aiPendingChangeType: 'update',
        aiPendingFields: ['content'],
      }, { localWrite: true, queryId });
    } else if (tc.name === 'deleteAttachment') {
      if (still.get(attachmentId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = attachments[attachmentId];
      if (!serverDoc) continue;
      still.set(attachmentId, { queryId, type: 'delete' });
      firestoreRedux.save('card-attachments', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'delete',
      }, { localWrite: true, queryId });
    }
  }

  // Roll back overlays no longer pending: a create was just a preview, so drop it; an
  // update or delete only added markers to a real attachment, so strip them to restore it.
  for (const [attachmentId, { queryId, type }] of previously) {
    if (still.has(attachmentId)) continue;
    if (type === 'create') {
      // Approved → firestore-redux swaps the server attachment in over the localWrite, so
      // leave it; keep an in-progress one tracked so a later fail still drops it. Only a
      // reject/cancel/fail deletes (success falls through — the server doc has taken over).
      const createStatus = approvedCreates.get(attachmentId);
      if (createStatus === 'IN_PROGRESS') still.set(attachmentId, { queryId, type: 'create' });
      else if (!createStatus) firestoreRedux.delete('card-attachments', attachmentId, { localWrite: true, queryId });
    } else {
      const current = attachments[attachmentId];
      if (current) firestoreRedux.save('card-attachments', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }

  aiPendingEditsAttachmentIds[boardId] = still;
}

/**
 * Writes + cleans up the chat-message preview overlays: createChatMessage localWrites a
 * new card-chat-messages doc (its AI-supplied chatMessageId) with the proposed text;
 * updateChatMessage overlays the proposed text (+ oldMessage snapshot) onto the existing
 * one. Overlays no longer pending are rolled back — a create is dropped, an update is
 * restored to the underlying doc.
 *
 * @param {Object} state - Redux state from the start of the run.
 * @param {String} boardId
 * @param {Array} toolCalls - every pending-edit chat's tool calls (flattened).
 */
function applyChatPreviewModel(state, boardId, toolCalls) {
  const previously = aiPendingEditsChatIds[boardId] || new Map();
  const still = new Map(); // chatMessageId → { queryId, type: 'create' | 'update' }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createChatMessage', (tc) => tc.input?.chatMessageId);
  const messages = firestoreRedux.selectors.collection(state, 'card-chat-messages') || {};
  // The proposed comment is added on the current user's behalf — stamp them as its author so
  // it reads as the user's own message (right-aligned) from the start, instead of flashing on
  // the left (someone else's) until the server's committed doc — which also names the user —
  // lands over the localWrite.
  const currentUserId = auth.selectors.currentUserId(state);
  // The AI often sends newlines as the literal escape "\n"; turn them into real line
  // breaks so kerika-markdown renders them (and the diff compares like-for-like).
  const unescapeNewlines = (text) => (text || '').replace(/\\n/g, '\n');

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    const cardId = tc.input?.cardId;
    const chatMessageId = tc.input?.chatMessageId;
    if (!cardId || !chatMessageId) continue;
    const queryId = `card-chats-${cardId}`;

    if (tc.name === 'createChatMessage') {
      still.set(chatMessageId, { queryId, type: 'create' });
      firestoreRedux.save('card-chat-messages', {
        id: chatMessageId,
        cardId,
        boardId,
        message: unescapeNewlines((tc.editedInput || tc.input).text),
        // Read as the editor's HTML, as the server's doc is: its mention and reference anchors are chips from the start.
        messageHtml: proposalEditorHtml(unescapeNewlines((tc.editedInput || tc.input).text)),
        referencedUsers: [],
        referencedUsersAll: false,
        createdAt: tc.invokedAt || Date.now(),
        createdBy: currentUserId,
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateChatMessage' && 'text' in tc.input) {
      if (still.get(chatMessageId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = messages[chatMessageId];
      if (!serverDoc) continue;
      still.set(chatMessageId, { queryId, type: 'update' });
      // Keep the oldMessage snapshot once set so it survives re-runs — on a re-run the doc
      // here is already the overlay, whose `message` is the proposed one.
      const oldMessage = serverDoc.oldMessage !== undefined ? serverDoc.oldMessage : chatMessageEditorValue(serverDoc);
      firestoreRedux.save('card-chat-messages', {
        ...serverDoc,
        message: unescapeNewlines((tc.editedInput || tc.input).text),
        oldMessage,
        aiPendingChangeType: 'update',
        aiPendingFields: ['message'],
      }, { localWrite: true, queryId });
    } else if (tc.name === 'deleteChatMessage') {
      if (still.get(chatMessageId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = messages[chatMessageId];
      if (!serverDoc) continue;
      still.set(chatMessageId, { queryId, type: 'delete' });
      firestoreRedux.save('card-chat-messages', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'delete',
      }, { localWrite: true, queryId });
    }
  }

  // Roll back overlays no longer pending: a create was just a preview, so drop it; an
  // update or delete only added markers to a real message, so strip them to restore it.
  for (const [chatMessageId, { queryId, type }] of previously) {
    if (still.has(chatMessageId)) continue;
    if (type === 'create') {
      // Approved → firestore-redux swaps the server message in over the localWrite, so
      // leave it; keep an in-progress one tracked so a later fail still drops it. Only a
      // reject/cancel/fail deletes (success falls through — the server doc has taken over).
      const createStatus = approvedCreates.get(chatMessageId);
      if (createStatus === 'IN_PROGRESS') still.set(chatMessageId, { queryId, type: 'create' });
      else if (!createStatus) firestoreRedux.delete('card-chat-messages', chatMessageId, { localWrite: true, queryId });
    } else {
      const current = messages[chatMessageId];
      const approvedFields = type === 'update' ? approvedMessageFields(state, toolCalls, 'updateChatMessage', chatMessageId) : null;
      if (current) firestoreRedux.save('card-chat-messages', stripAiPendingOverlay(current, approvedFields), { localWrite: true, queryId });
    }
  }

  aiPendingEditsChatIds[boardId] = still;
}

// Board tags → localWrite overlays so the tags list shows the proposal: create writes a preview
// tag, update overlays name/colour with an old<Field> snapshot, delete flags it, merge marks
// each source tag going into the target. Rolled back once the proposal leaves WAITING. The
// pattern below (create / update+snapshot / delete / rollback) repeats for every board surface.
function applyBoardTagPreviewModel(state, boardId, toolCalls) {
  const queryId = `board-tags-${boardId}`;
  const previously = aiPendingEditsBoardTagIds[boardId] || new Map();
  const still = new Map(); // boardTagId → { type: 'create' | 'update' | 'delete' | 'merge' }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createBoardTag', (tc) => tc.input?.boardTagId);
  const tags = firestoreRedux.selectors.collection(state, 'board-tags') || {};

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    if (tc.input?.boardId !== boardId) continue;

    if (tc.name === 'createBoardTag') {
      const boardTagId = tc.input?.boardTagId;
      if (!boardTagId) continue;
      still.set(boardTagId, { type: 'create' });
      firestoreRedux.save('board-tags', {
        id: boardTagId,
        boardId,
        name: tc.input.name || '',
        color: tc.input.color || null,
        createdAt: tc.invokedAt || Date.now(),
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateBoardTag') {
      const boardTagId = tc.input?.boardTagId;
      if (!boardTagId) continue;
      if (still.get(boardTagId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = tags[boardTagId];
      if (!serverDoc) continue;
      // null = leave unchanged, so overlay only the fields actually given.
      const proposed = {};
      const oldSnapshot = {};
      const aiPendingFields = [];
      for (const field of ['name', 'color']) {
        if (tc.input?.[field] == null) continue;
        proposed[field] = tc.input[field];
        const oldKey = oldFieldKey(field);
        oldSnapshot[oldKey] = serverDoc[oldKey] !== undefined ? serverDoc[oldKey] : serverDoc[field];
        aiPendingFields.push(field);
      }
      if (!aiPendingFields.length) continue;
      still.set(boardTagId, { type: 'update' });
      firestoreRedux.save('board-tags', {
        ...serverDoc,
        ...proposed,
        ...oldSnapshot,
        aiPendingChangeType: 'update',
        aiPendingFields,
      }, { localWrite: true, queryId });
    } else if (tc.name === 'deleteBoardTag') {
      const boardTagId = tc.input?.boardTagId;
      if (!boardTagId) continue;
      if (still.get(boardTagId)?.type === 'create') continue;
      const serverDoc = tags[boardTagId];
      if (!serverDoc) continue;
      still.set(boardTagId, { type: 'delete' });
      firestoreRedux.save('board-tags', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'delete',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'mergeBoardTags') {
      // Each source tag goes into the target — mark it merging and name the tag it joins.
      const sourceTagIds = Array.isArray(tc.input?.sourceTagIds) ? tc.input.sourceTagIds : [];
      const targetTagId = tc.input?.targetTagId;
      for (const sourceTagId of sourceTagIds) {
        if (still.get(sourceTagId)?.type === 'create') continue;
        const serverDoc = tags[sourceTagId];
        if (!serverDoc) continue;
        still.set(sourceTagId, { type: 'merge' });
        firestoreRedux.save('board-tags', {
          ...serverDoc,
          aiPendingDelete: true,
          aiPendingChangeType: 'merge',
          aiPendingMergeInto: targetTagId,
        }, { localWrite: true, queryId });
      }
    }
  }

  // No longer pending: drop a create (it was only a preview), strip the markers off the rest.
  for (const [boardTagId, { type }] of previously) {
    if (still.has(boardTagId)) continue;
    if (type === 'create') {
      const createStatus = approvedCreates.get(boardTagId);
      if (createStatus === 'IN_PROGRESS') still.set(boardTagId, { type: 'create' });
      else if (!createStatus) firestoreRedux.delete('board-tags', boardTagId, { localWrite: true, queryId });
    } else {
      const current = tags[boardTagId];
      if (current) firestoreRedux.save('board-tags', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }

  aiPendingEditsBoardTagIds[boardId] = still;
}

// Board columns → overlays, same shape as tags: create / update (name, wipLimit, order + their
// old<Field> snapshots) / delete. wipLimit 0 clears the limit, so only null counts as unchanged.
// `hidden` is per-user (a separate record), so it's left to the visibility task.
function applyBoardColumnPreviewModel(state, boardId, toolCalls) {
  const queryId = `board_columns_${boardId}`;
  const previously = aiPendingEditsColumnIds[boardId] || new Map();
  const still = new Map(); // columnId → { type: 'create' | 'update' | 'delete' }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createColumn', (tc) => tc.input?.columnId);
  const cols = firestoreRedux.selectors.collection(state, 'columns') || {};

  // Visibility is per-user (a `hidden-columns` doc), so a show/hide proposal is previewed by
  // overlaying that doc too — the column reads as hidden/shown for the reviewer while they decide.
  const userId = auth.selectors.currentUserId(state);
  const visibilityQueryId = `hidden-columns-${boardId}`;
  const previousVisibility = aiPendingEditsColumnVisibility[boardId] || new Map();
  const stillVisibility = new Map(); // columnId → { original, docId }
  const hiddenColumnDocs = firestoreRedux.selectors.docsByQueryResult(state, visibilityQueryId, 'hidden-columns') || [];

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    if (tc.input?.boardId !== boardId) continue;
    const columnId = tc.input?.columnId;
    if (!columnId) continue;

    if (tc.name === 'createColumn') {
      still.set(columnId, { type: 'create' });
      // The tool appends the column last when `order` is omitted. Board column orders are sequential
      // with Done / Trash pinned at the end (order 9999 / 10000), so one past the column count sorts
      // into the last workflow slot — before Done / Trash, after the other columns.
      const order = tc.input.order != null ? tc.input.order : (selectors.columns(state, boardId) || []).length + 1;
      firestoreRedux.save('columns', {
        id: columnId,
        boardId,
        name: tc.input.name || '',
        order,
        type: 'OTHER',
        createdAt: tc.invokedAt || Date.now(),
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateColumn') {
      if (still.get(columnId)?.type === 'create') continue; // a create already wrote it
      const serverDoc = cols[columnId];
      if (!serverDoc) continue;
      const proposed = {};
      const oldSnapshot = {};
      const aiPendingFields = [];
      for (const field of ['name', 'wipLimit', 'order']) {
        if (tc.input?.[field] == null) continue;
        proposed[field] = tc.input[field];
        const oldKey = oldFieldKey(field);
        oldSnapshot[oldKey] = serverDoc[oldKey] !== undefined ? serverDoc[oldKey] : serverDoc[field];
        aiPendingFields.push(field);
      }
      // Visibility is per-user, so it's recorded as a pending field (so the change renders) and
      // previewed by overlaying the reviewer's own `hidden-columns` doc, so the column actually
      // reads as hidden/shown while they decide. The original doc is kept for the revert.
      if (tc.input?.hidden != null) {
        aiPendingFields.push('hidden');
        const existing = hiddenColumnDocs.find((doc) => doc.columnId === columnId);
        const original = previousVisibility.has(columnId)
          ? previousVisibility.get(columnId).original
          : (existing ? { ...existing } : null);
        const docId = existing ? existing.id : `bhc_ai_${getIdWoPrefix({ id: columnId, prefix: 'col_' })}`;
        firestoreRedux.save('hidden-columns',
          { id: docId, userId, boardId, columnId, hidden: !!tc.input.hidden },
          { localWrite: true, queryId: visibilityQueryId });
        stillVisibility.set(columnId, { original, docId });
      }
      if (!aiPendingFields.length) continue;
      still.set(columnId, { type: 'update' });
      firestoreRedux.save('columns', {
        ...serverDoc,
        ...proposed,
        ...oldSnapshot,
        aiPendingChangeType: 'update',
        aiPendingFields,
      }, { localWrite: true, queryId });
    } else if (tc.name === 'deleteColumn') {
      if (still.get(columnId)?.type === 'create') continue;
      const serverDoc = cols[columnId];
      if (!serverDoc) continue;
      still.set(columnId, { type: 'delete' });
      firestoreRedux.save('columns', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'delete',
      }, { localWrite: true, queryId });
    }
  }

  for (const [columnId, { type }] of previously) {
    if (still.has(columnId)) continue;
    if (type === 'create') {
      const createStatus = approvedCreates.get(columnId);
      if (createStatus === 'IN_PROGRESS') still.set(columnId, { type: 'create' });
      else if (!createStatus) firestoreRedux.delete('columns', columnId, { localWrite: true, queryId });
    } else {
      const current = cols[columnId];
      if (current) firestoreRedux.save('columns', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }

  // Revert a visibility overlay whose proposal is gone — restore the reviewer's own doc, or drop
  // the one we synthesised (a hide on a column that had no `hidden-columns` doc).
  for (const [columnId, { original, docId }] of previousVisibility) {
    if (stillVisibility.has(columnId)) continue;
    if (original) firestoreRedux.save('hidden-columns', original, { localWrite: true, queryId: visibilityQueryId });
    else firestoreRedux.delete('hidden-columns', docId, { localWrite: true, queryId: visibilityQueryId });
  }

  aiPendingEditsColumnIds[boardId] = still;
  aiPendingEditsColumnVisibility[boardId] = stillVisibility;
}

/** A board-team-member doc id — `btm_${board}_${user}` (both ids stripped of their prefixes). */
const teamMemberDocId = (boardId, userId) =>
  `btm_${getIdWoPrefix({ id: boardId, prefix: 'brd_' })}_${getIdWoPrefix({ id: userId, prefix: 'usr_' })}`;

// A team tool identifies the member by `userId`, or — when adding someone by email — by `email`
// (resolved to the user's id via the loaded `users` collection; a brand-new invitee has none yet).
function teamToolUserId(state, tc) {
  if (tc.input?.userId) return tc.input.userId;
  return userIdByEmail(firestoreRedux.selectors.collection(state, 'users'), tc.input?.email);
}

// Board team → overlays: add writes a membership, role change overlays `role` with an `oldRole`
// snapshot, remove flags it (change type 'remove', never 'delete', per the tool). The membership
// doc is found by its derived id `btm_${board}_${user}`.
function applyBoardTeamPreviewModel(state, boardId, toolCalls) {
  const queryId = `board-team-members_boardId-${boardId}`;
  const previously = aiPendingEditsTeamIds[boardId] || new Map();
  const still = new Map(); // memberId → { type: 'create' | 'update' | 'remove' }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'addBoardTeamMember', (tc) => teamMemberDocId(boardId, teamToolUserId(state, tc)));
  const members = firestoreRedux.selectors.collection(state, 'board-team-members') || {};

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    if (tc.input?.boardId !== boardId) continue;
    const userId = teamToolUserId(state, tc);
    if (!userId) continue;
    const memberId = teamMemberDocId(boardId, userId);

    if (tc.name === 'addBoardTeamMember') {
      still.set(memberId, { type: 'create' });
      // A proposed member isn't in the board team yet, so the board-members subscription never
      // loads their profile — fetch it here so the diff row has an avatar / name.
      if (!firestoreRedux.selectors.doc(state, 'users', userId)) {
        firestoreRedux.getDocById('users', userId, { requesterId: queryId });
      }
      firestoreRedux.save('board-team-members', {
        id: memberId,
        boardId,
        userId,
        role: tc.input.role || 'TEAM_MEMBER',
        joinedAt: tc.invokedAt || Date.now(),
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateBoardTeamMemberRole') {
      if (still.get(memberId)?.type === 'create') continue;
      if (tc.input?.role == null) continue;
      const serverDoc = members[memberId];
      if (!serverDoc) continue;
      still.set(memberId, { type: 'update' });
      const oldRole = serverDoc.oldRole !== undefined ? serverDoc.oldRole : serverDoc.role;
      firestoreRedux.save('board-team-members', {
        ...serverDoc,
        role: tc.input.role,
        oldRole,
        aiPendingChangeType: 'update',
        aiPendingFields: ['role'],
      }, { localWrite: true, queryId });
    } else if (tc.name === 'removeBoardTeamMember') {
      if (still.get(memberId)?.type === 'create') continue;
      const serverDoc = members[memberId];
      if (!serverDoc) continue;
      still.set(memberId, { type: 'remove' });
      firestoreRedux.save('board-team-members', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'remove',
      }, { localWrite: true, queryId });
    }
  }

  for (const [memberId, { type }] of previously) {
    if (still.has(memberId)) continue;
    if (type === 'create') {
      const createStatus = approvedCreates.get(memberId);
      if (createStatus === 'IN_PROGRESS') still.set(memberId, { type: 'create' });
      else if (!createStatus) firestoreRedux.delete('board-team-members', memberId, { localWrite: true, queryId });
    } else {
      const current = members[memberId];
      if (current) firestoreRedux.save('board-team-members', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }

  aiPendingEditsTeamIds[boardId] = still;
}

// Board attachments → overlays (links only, no uploads): create writes a preview (sorted to the
// top), update overlays `title` with an `oldTitle` snapshot, delete flags it.
function applyBoardAttachmentPreviewModel(state, boardId, toolCalls) {
  const queryId = `board-attachments-${boardId}`;
  const previously = aiPendingEditsBoardAttachmentIds[boardId] || new Map();
  const still = new Map(); // attachmentId → { type }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createBoardAttachment', (tc) => tc.input?.attachmentId);
  const attachments = firestoreRedux.selectors.collection(state, 'board-attachments') || {};
  let newAttachmentOrder = -100;

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    if (tc.input?.boardId !== boardId) continue;
    const attachmentId = tc.input?.attachmentId;
    if (!attachmentId) continue;

    if (tc.name === 'createBoardAttachment') {
      const preview = resolveAttachmentPreview(boardId, tc.input.url);
      still.set(attachmentId, { type: 'create' });
      firestoreRedux.save('board-attachments', {
        id: attachmentId,
        boardId,
        // A null title means the server resolves it from the URL on approval; until then show the
        // URL so the preview row isn't blank.
        title: tc.input.title || tc.input.url || '',
        url: tc.input.url || '',
        type: 'EXTERNAL_LINK',
        subType: preview.subType || 'WEB',
        previewAvailable: !!preview.previewAvailable,
        order: newAttachmentOrder--,
        createdAt: tc.invokedAt || Date.now(),
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateBoardAttachmentTitle' && 'title' in tc.input) {
      if (still.get(attachmentId)?.type === 'create') continue;
      const serverDoc = attachments[attachmentId];
      if (!serverDoc) continue;
      still.set(attachmentId, { type: 'update' });
      const oldTitle = serverDoc.oldTitle !== undefined ? serverDoc.oldTitle : serverDoc.title;
      firestoreRedux.save('board-attachments', {
        ...serverDoc,
        title: tc.input.title || '',
        oldTitle,
        aiPendingChangeType: 'update',
        aiPendingFields: ['title'],
      }, { localWrite: true, queryId });
    } else if (tc.name === 'deleteBoardAttachment') {
      if (still.get(attachmentId)?.type === 'create') continue;
      const serverDoc = attachments[attachmentId];
      if (!serverDoc) continue;
      still.set(attachmentId, { type: 'delete' });
      firestoreRedux.save('board-attachments', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'delete',
      }, { localWrite: true, queryId });
    }
  }

  for (const [attachmentId, { type }] of previously) {
    if (still.has(attachmentId)) continue;
    if (type === 'create') {
      const createStatus = approvedCreates.get(attachmentId);
      if (createStatus === 'IN_PROGRESS') still.set(attachmentId, { type: 'create' });
      else if (!createStatus) firestoreRedux.delete('board-attachments', attachmentId, { localWrite: true, queryId });
    } else {
      const current = attachments[attachmentId];
      if (current) firestoreRedux.save('board-attachments', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }

  aiPendingEditsBoardAttachmentIds[boardId] = still;
}

// Board chat → overlays: create writes a message, update overlays `message` with an `oldMessage`
// snapshot, delete flags it. The chat loads a page at a time, so an off-page target message is
// fetched by id first and overlaid when it lands.
function applyBoardChatPreviewModel(state, boardId, toolCalls) {
  const queryId = `board-chats-${boardId}`;
  const previously = aiPendingEditsBoardChatIds[boardId] || new Map();
  const still = new Map(); // chatMessageId → { type }
  const approvedCreates = collectApprovedCreates(state, toolCalls, 'createBoardChatMessage', (tc) => tc.input?.chatMessageId);
  const messages = firestoreRedux.selectors.collection(state, 'board-chat-messages') || {};
  // Post it under the current user so it reads as their own message (right-aligned), not flashing
  // on the left until the committed doc lands. Same as the card chat fix.
  const currentUserId = auth.selectors.currentUserId(state);
  // The AI often sends newlines as the literal escape "\n"; turn them into real line breaks.
  const unescapeNewlines = (text) => (text || '').replace(/\\n/g, '\n');
  const requestedLoads = aiPendingEditsBoardChatLoadRequested[boardId] || new Set();
  const toLoad = [];

  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    if (tc.input?.boardId !== boardId) continue;
    const chatMessageId = tc.input?.chatMessageId;
    if (!chatMessageId) continue;

    if (tc.name === 'createBoardChatMessage') {
      still.set(chatMessageId, { type: 'create' });
      firestoreRedux.save('board-chat-messages', {
        id: chatMessageId,
        boardId,
        message: unescapeNewlines((tc.editedInput || tc.input).text),
        // Read as the editor's HTML, as the server's doc is: its mention and reference anchors are chips from the start.
        messageHtml: proposalEditorHtml(unescapeNewlines((tc.editedInput || tc.input).text)),
        referencedUsers: [],
        referencedUsersAll: false,
        createdAt: tc.invokedAt || Date.now(),
        createdBy: currentUserId,
        aiPendingChangeType: 'create',
      }, { localWrite: true, queryId });
    } else if (tc.name === 'updateBoardChatMessage' && 'text' in tc.input) {
      if (still.get(chatMessageId)?.type === 'create') continue;
      const serverDoc = messages[chatMessageId];
      if (!serverDoc) { if (!requestedLoads.has(chatMessageId)) toLoad.push(chatMessageId); continue; }
      still.set(chatMessageId, { type: 'update' });
      const oldMessage = serverDoc.oldMessage !== undefined ? serverDoc.oldMessage : chatMessageEditorValue(serverDoc);
      firestoreRedux.save('board-chat-messages', {
        ...serverDoc,
        message: unescapeNewlines((tc.editedInput || tc.input).text),
        oldMessage,
        aiPendingChangeType: 'update',
        aiPendingFields: ['message'],
      }, { localWrite: true, queryId });
    } else if (tc.name === 'deleteBoardChatMessage') {
      if (still.get(chatMessageId)?.type === 'create') continue;
      const serverDoc = messages[chatMessageId];
      if (!serverDoc) { if (!requestedLoads.has(chatMessageId)) toLoad.push(chatMessageId); continue; }
      still.set(chatMessageId, { type: 'delete' });
      firestoreRedux.save('board-chat-messages', {
        ...serverDoc,
        aiPendingDelete: true,
        aiPendingChangeType: 'delete',
      }, { localWrite: true, queryId });
    }
  }

  // Fetch off-page target messages by id (once each); the subscription re-applies when they land.
  if (toLoad.length) {
    for (const id of toLoad) requestedLoads.add(id);
    aiPendingEditsBoardChatLoadRequested[boardId] = requestedLoads;
    store.dispatch({ type: 'CHAT_LOAD_MATCHED_BOARD_CHATS', requesterId: `board-page-${boardId}`, boardId, chatIds: toLoad });
  }

  for (const [chatMessageId, { type }] of previously) {
    if (still.has(chatMessageId)) continue;
    if (type === 'create') {
      const createStatus = approvedCreates.get(chatMessageId);
      if (createStatus === 'IN_PROGRESS') still.set(chatMessageId, { type: 'create' });
      else if (!createStatus) firestoreRedux.delete('board-chat-messages', chatMessageId, { localWrite: true, queryId });
    } else {
      const current = messages[chatMessageId];
      const approvedFields = type === 'update' ? approvedMessageFields(state, toolCalls, 'updateBoardChatMessage', chatMessageId) : null;
      if (current) firestoreRedux.save('board-chat-messages', stripAiPendingOverlay(current, approvedFields), { localWrite: true, queryId });
    }
  }

  aiPendingEditsBoardChatIds[boardId] = still;
}

// Board-settings tool → the board doc field it changes + the input key holding the value. The
// toggles use `enabled`; auto-numbering's field is `showCardRefNo`. updateBoardInViewSettings is
// left out — it's per-user (dashboard) state, not a board attribute.
const BOARD_SETTING_FIELD = {
  updateBoardName: { field: 'name', inputKey: 'name' },
  updateBoardDescription: { field: 'description', inputKey: 'description' },
  updateBoardPrivacy: { field: 'privacy', inputKey: 'privacy' },
  updateBoardWipLimitEnabled: { field: 'wipLimitEnabled', inputKey: 'enabled' },
  updateBoardAutoNumbering: { field: 'showCardRefNo', inputKey: 'enabled' },
  updateBoardTagsEnabled: { field: 'tagsEnabled', inputKey: 'enabled' },
};

// Board settings → each proposed setting overlaid on the one board doc with an old<Field>
// snapshot (the doc is read by id, so no queryId); stripped once nothing is proposed.
function applyBoardSettingPreviewModel(state, boardId, toolCalls) {
  const proposed = {};
  const oldFieldNames = new Set();
  for (const tc of toolCalls) {
    if (!isAwaitingApproval(state, tc)) continue;
    if (tc.input?.boardId !== boardId) continue;
    const mapping = BOARD_SETTING_FIELD[tc.name];
    if (!mapping || !(mapping.inputKey in (tc.input || {}))) continue;
    proposed[mapping.field] = tc.input[mapping.inputKey];
    oldFieldNames.add(mapping.field);
  }

  const boardDoc = firestoreRedux.selectors.doc(state, 'boards', boardId);

  if (oldFieldNames.size) {
    if (!boardDoc) return;
    const overlay = { ...boardDoc };
    // Revert a field overlaid earlier whose proposal resolved while a sibling still waits, so its
    // stale value doesn't ride along below.
    for (const field of overlay.aiPendingFields || []) {
      if (oldFieldNames.has(field)) continue;
      const oldKey = oldFieldKey(field);
      if (oldKey in overlay) overlay[field] = overlay[oldKey];
      delete overlay[oldKey];
    }
    const oldSnapshot = {};
    for (const field of oldFieldNames) {
      const oldKey = oldFieldKey(field);
      oldSnapshot[oldKey] = oldKey in overlay ? overlay[oldKey] : overlay[field];
    }
    firestoreRedux.save('boards', {
      ...overlay,
      ...proposed,
      ...oldSnapshot,
      aiPendingChangeType: 'update',
      aiPendingFields: [...oldFieldNames],
    }, { localWrite: true });
    aiPendingEditsSettingsBoardIds[boardId] = true;
  } else if (aiPendingEditsSettingsBoardIds[boardId]) {
    if (boardDoc) firestoreRedux.save('boards', stripAiPendingOverlay(boardDoc), { localWrite: true });
    delete aiPendingEditsSettingsBoardIds[boardId];
  }
}

/**
 * Records a no-longer-live card-field proposal, keeping only the most recently invoked
 * one per card + field. A field can carry several resolved proposals over a chat's life
 * (an old approved one, a newer rejected one); only the latest describes the overlay
 * that cleanup is about to take down.
 */
function noteResolvedEditProposal(byCardId, cardId, tc) {
  // A cross-board moveCard overlays both `columnId` and `boardId`, so record both as resolved.
  // Otherwise an approved cross-board move keeps the proposed column but reverts the board,
  // stranding the card on the source board with a destination column. (A same-board move only
  // overlays `columnId`, so the extra `boardId` here is a harmless no-op for it.)
  const fields = tc.name === 'moveCard' ? ['columnId', 'boardId'] : [resolveCardEditField(tc)];
  const proposalsByField = byCardId.get(cardId) || new Map();
  for (const field of fields) {
    if (!field) continue;
    const recordedProposal = proposalsByField.get(field);
    if (recordedProposal && (recordedProposal.invokedAt || 0) > (tc.invokedAt || 0)) continue;
    proposalsByField.set(field, tc);
  }
  byCardId.set(cardId, proposalsByField);
}

/** Card fields whose latest resolved proposal was approved — the overlay holds the value
 *  the server is committing, so cleanup keeps it instead of reverting. */
function collectApprovedEditFields(state, resolvedEdits, cardId) {
  const proposalsByField = resolvedEdits.get(cardId);
  if (!proposalsByField) return null;
  const approvedFields = new Set();
  for (const [field, tc] of proposalsByField) {
    if (isApproved(state, tc)) approvedFields.add(field);
  }
  return approvedFields.size ? approvedFields : null;
}

function collectEditProposal(byCardId, cardId, proposed, oldFieldNames) {
  const entry = byCardId.get(cardId) || { proposed: {}, oldFieldNames: new Set() };
  Object.assign(entry.proposed, proposed);
  for (const f of oldFieldNames) entry.oldFieldNames.add(f);
  byCardId.set(cardId, entry);
}

/**
 * Fans the AI-proposed `createCard` sub-entities (subtasks, attachments,
 * chat messages) into their respective collections as localWrites so the
 * card-detail dialog's tabs render them while the parent card is still in
 * WAITING. Tracked per-card so cleanup can drop them all when the parent
 * leaves WAITING.
 */
/**
 * The timestamp an AI Helper tool's `dueDate` proposes on `boardId`, under its account's due date
 * default - the value the server stores once the proposal is approved.
 */
function toolDueTime(state, boardId, dueDate) {
  const accountId = selectors.accountIdOfBoard(state, boardId);
  return resolveToolDueTime(dueDate, manageAccountSelectors.dueDateDefault({ state, accountId }));
}

function writeCreateCardBundle(boardId, cardId, tc) {
  const tasksQueryId = `tasks-${boardId}-${cardId}`;
  const attachmentsQueryId = `card-attachments-${cardId}`;
  const chatsQueryId = `card-chats-${cardId}`;
  const summaryQueryId = `cards_summary_${boardId}`;

  const bundle = { tasks: [], attachments: [], chatMessages: [], summaryId: null };

  const subtasks = Array.isArray(tc.input?.subtasks) ? tc.input.subtasks : [];
  const state = store.getState();
  let openTaskCount = 0;
  let closedTaskCount = 0;
  subtasks.forEach((st, i) => {
    if (!st?.subtaskId) return;
    bundle.tasks.push({ id: st.subtaskId, queryId: tasksQueryId });
    if (st.markedDone) closedTaskCount++; else openTaskCount++;
    firestoreRedux.save('tasks', {
      id: st.subtaskId,
      cardId,
      boardId,
      title: st.title || '',
      order: st.order != null ? st.order : i + 1,
      closed: !!st.markedDone,
      dueDate: toolDueTime(state, boardId, st.dueDate),
      assignments: Array.isArray(st.assignees) ? st.assignees : [],
      createdAt: tc.invokedAt || Date.now(),
      createdBy: st.createdBy || null,
    }, { localWrite: true, queryId: tasksQueryId });
  });

  const attachments = Array.isArray(tc.input?.attachments) ? tc.input.attachments : [];
  attachments.forEach((att) => {
    if (!att?.attachmentId) return;
    bundle.attachments.push({ id: att.attachmentId, queryId: attachmentsQueryId });
    firestoreRedux.save('card-attachments', {
      id: att.attachmentId,
      cardId,
      boardId,
      title: att.title || att.name || '',
      contentType: att.contentType || '',
      type: att.type || '',
      subType: att.subType || '',
      url: att.url || '',
      order: att.order != null ? att.order : -Date.now(),
      createdAt: tc.invokedAt || Date.now(),
    }, { localWrite: true, queryId: attachmentsQueryId });
  });

  const chatMessages = Array.isArray(tc.input?.chatMessages) ? tc.input.chatMessages : [];
  chatMessages.forEach((cm) => {
    if (!cm?.chatMessageId) return;
    bundle.chatMessages.push({ id: cm.chatMessageId, queryId: chatsQueryId });
    firestoreRedux.save('card-chat-messages', {
      id: cm.chatMessageId,
      cardId,
      boardId,
      message: cm.text || cm.message || '',
      messageHtml: markdownToHtml(cm.text || cm.message),
      referencedUsers: cm.referencedUsers || [],
      referencedUsersAll: cm.referencedUsersAll || false,
      createdAt: cm.createdAt || tc.invokedAt || Date.now(),
      createdBy: cm.createdBy || null,
    }, { localWrite: true, queryId: chatsQueryId });
  });

  // cards-summary delta — counts surface in the card's meta-data icons
  // (description / tasks / chats / attachments badges) on the board. The
  // summary doc id is `cs_<cardId without crd_ prefix>`.
  const summaryId = `cs_${getIdWoPrefix({ id: cardId, prefix: 'crd_' })}`;
  bundle.summaryId = summaryId;
  firestoreRedux.save('cards-summary', {
    id: summaryId,
    cardId,
    boardId,
    columnId: tc.input.columnId,
    columnType: 'OTHER',
    columnOrder: tc.input.columnOrder,
    chats: chatMessages.length,
    attachments: attachments.length,
    openTasks: openTaskCount,
    closedTasks: closedTaskCount,
    tasks: { open: openTaskCount, closed: closedTaskCount },
    tags: Array.isArray(tc.input.tags) ? tc.input.tags : [],
  }, { localWrite: true, queryId: summaryQueryId });

  if (!aiPendingEditsBundles[boardId]) aiPendingEditsBundles[boardId] = {};
  aiPendingEditsBundles[boardId][cardId] = bundle;
}

/**
 * Drops every bundled sub-entity localWrite for the given (boardId, cardId).
 * Called when a NEW card leaves WAITING (apply pass cleanup) and when the
 * board itself is unloaded (`unsubscribeAiPendingEdits`).
 */
function deleteCreateCardBundle(boardId, cardId) {
  const bundle = aiPendingEditsBundles[boardId] && aiPendingEditsBundles[boardId][cardId];
  if (!bundle) return;
  for (const { id, queryId } of bundle.tasks) {
    firestoreRedux.delete('tasks', id, { localWrite: true, queryId });
  }
  for (const { id, queryId } of bundle.attachments) {
    firestoreRedux.delete('card-attachments', id, { localWrite: true, queryId });
  }
  for (const { id, queryId } of bundle.chatMessages) {
    firestoreRedux.delete('card-chat-messages', id, { localWrite: true, queryId });
  }
  if (bundle.summaryId) {
    firestoreRedux.delete('cards-summary', bundle.summaryId, {
      localWrite: true,
      queryId: `cards_summary_${boardId}`,
    });
  }
  delete aiPendingEditsBundles[boardId][cardId];
}

function unsubscribeAiPendingEdits(boardId) {
  if (aiPendingEditsUnsubscribers[boardId]) {
    aiPendingEditsUnsubscribers[boardId]();
    delete aiPendingEditsUnsubscribers[boardId];
  }
  // Drop any localWrite overlays we created so they don't bleed into the next
  // board view. NEW overlays → drop the doc; EDIT / DELETE overlays → restore
  // the plain server doc (the doc itself shouldn't disappear).
  const state = store.getState();
  const cardsQueryId = `cards_${boardId}`;
  for (const cardId of aiPendingEditsLocalCardIds[boardId] || []) {
    firestoreRedux.delete('cards', cardId, { localWrite: true, queryId: cardsQueryId });
    deleteCreateCardBundle(boardId, cardId);
  }
  for (const cardId of aiPendingEditsEditCardIds[boardId] || []) {
    const currentCard = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (currentCard) {
      firestoreRedux.save('cards', stripAiPendingOverlay(currentCard), { localWrite: true, queryId: cardsQueryId });
    }
  }
  for (const cardId of aiPendingEditsDeleteCardIds[boardId] || []) {
    const currentCard = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (currentCard) {
      firestoreRedux.save('cards', stripAiPendingOverlay(currentCard), { localWrite: true, queryId: cardsQueryId });
    }
  }
  for (const [subtaskId, { queryId, type }] of aiPendingEditsSubtaskIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('tasks', subtaskId, { localWrite: true, queryId });
    } else {
      const currentTask = firestoreRedux.selectors.doc(state, 'tasks', subtaskId);
      if (currentTask) firestoreRedux.save('tasks', stripAiPendingOverlay(currentTask), { localWrite: true, queryId });
    }
  }
  for (const [attachmentId, { queryId, type }] of aiPendingEditsAttachmentIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('card-attachments', attachmentId, { localWrite: true, queryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'card-attachments', attachmentId);
      if (current) firestoreRedux.save('card-attachments', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }
  for (const [chatMessageId, { queryId, type }] of aiPendingEditsChatIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('card-chat-messages', chatMessageId, { localWrite: true, queryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'card-chat-messages', chatMessageId);
      if (current) firestoreRedux.save('card-chat-messages', stripAiPendingOverlay(current), { localWrite: true, queryId });
    }
  }
  const boardTagsQueryId = `board-tags-${boardId}`;
  for (const [boardTagId, { type }] of aiPendingEditsBoardTagIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('board-tags', boardTagId, { localWrite: true, queryId: boardTagsQueryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'board-tags', boardTagId);
      if (current) firestoreRedux.save('board-tags', stripAiPendingOverlay(current), { localWrite: true, queryId: boardTagsQueryId });
    }
  }
  const columnsQueryId = `board_columns_${boardId}`;
  for (const [columnId, { type }] of aiPendingEditsColumnIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('columns', columnId, { localWrite: true, queryId: columnsQueryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'columns', columnId);
      if (current) firestoreRedux.save('columns', stripAiPendingOverlay(current), { localWrite: true, queryId: columnsQueryId });
    }
  }
  const columnVisibilityQueryId = `hidden-columns-${boardId}`;
  for (const [, { original, docId }] of aiPendingEditsColumnVisibility[boardId] || []) {
    if (original) firestoreRedux.save('hidden-columns', original, { localWrite: true, queryId: columnVisibilityQueryId });
    else firestoreRedux.delete('hidden-columns', docId, { localWrite: true, queryId: columnVisibilityQueryId });
  }
  const teamQueryId = `board-team-members_boardId-${boardId}`;
  for (const [memberId, { type }] of aiPendingEditsTeamIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('board-team-members', memberId, { localWrite: true, queryId: teamQueryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'board-team-members', memberId);
      if (current) firestoreRedux.save('board-team-members', stripAiPendingOverlay(current), { localWrite: true, queryId: teamQueryId });
    }
  }
  const boardAttachmentsQueryId = `board-attachments-${boardId}`;
  for (const [attachmentId, { type }] of aiPendingEditsBoardAttachmentIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('board-attachments', attachmentId, { localWrite: true, queryId: boardAttachmentsQueryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'board-attachments', attachmentId);
      if (current) firestoreRedux.save('board-attachments', stripAiPendingOverlay(current), { localWrite: true, queryId: boardAttachmentsQueryId });
    }
  }
  if (aiPendingEditsSettingsBoardIds[boardId]) {
    const boardDoc = firestoreRedux.selectors.doc(state, 'boards', boardId);
    if (boardDoc) firestoreRedux.save('boards', stripAiPendingOverlay(boardDoc), { localWrite: true });
  }
  const boardChatsQueryId = `board-chats-${boardId}`;
  for (const [chatMessageId, { type }] of aiPendingEditsBoardChatIds[boardId] || []) {
    if (type === 'create') {
      firestoreRedux.delete('board-chat-messages', chatMessageId, { localWrite: true, queryId: boardChatsQueryId });
    } else {
      const current = firestoreRedux.selectors.doc(state, 'board-chat-messages', chatMessageId);
      if (current) firestoreRedux.save('board-chat-messages', stripAiPendingOverlay(current), { localWrite: true, queryId: boardChatsQueryId });
    }
  }
  delete aiPendingEditsLocalCardIds[boardId];
  delete aiPendingEditsEditCardIds[boardId];
  delete aiPendingEditsDeleteCardIds[boardId];
  delete aiPendingEditsBundles[boardId];
  delete aiPendingEditsSubtaskIds[boardId];
  delete aiPendingEditsAttachmentIds[boardId];
  delete aiPendingEditsChatIds[boardId];
  delete aiPendingEditsBoardTagIds[boardId];
  delete aiPendingEditsColumnIds[boardId];
  delete aiPendingEditsColumnVisibility[boardId];
  delete aiPendingEditsTeamIds[boardId];
  delete aiPendingEditsBoardAttachmentIds[boardId];
  delete aiPendingEditsSettingsBoardIds[boardId];
  delete aiPendingEditsBoardChatIds[boardId];
  delete aiPendingEditsBoardChatLoadRequested[boardId];
  delete aiPendingEditsReapply[boardId];
  delete aiPendingEditsApplyInFlight[boardId];
  delete aiPendingEditsLoading[boardId];
}

/**
 * Invoked when selected column is changed.
 * live listen on cardsByColumn of selected column.
 *  - on change check all selected cards in column or not.
 *  - card which is not available in the column then remove it from the card selection.
 */
function onSelectedColumnCardsChange() {
  const state = store.getState();
  const selectedCards = selectors.selectedCards(state);
  const selectedColumn = selectors.selectedColumn(state);
  if(isEmpty(selectedCards) || !selectedColumn) {
    return;
  }

  const cardsByColumn = selectors.cardsByColumn(state, selectedColumn) || [];
  if(isEmpty(cardsByColumn) || isEqual(lastCardsByColumn, cardsByColumn)) {
    return;
  }

  let newSelectedCards = selectedCards;
  lastCardsByColumn = cardsByColumn;
  forEach(selectedCards, (cardId) => {
    let cardFound = false;
    forEach(cardsByColumn, (card)=> {
      if(card.id === cardId) {
        cardFound = true;
        return false;
      }
    });

    if(!cardFound) {
      newSelectedCards = without(newSelectedCards, cardId);
    }
  });

  if(isEqual(newSelectedCards, selectedCards)) {
    return;
  }

  if(isEmpty(newSelectedCards)) {
    store.dispatch(actions.clearSelection());
  } else {
    store.dispatch(actions.setSelection(newSelectedCards, selectedColumn));
  }
}

/**
 * Mark welcomeDialogPresented as true.
 */
function* markUseTemplateWelcomeDialogAsPresented() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  yield call(markWelcomeDialogAsPresented);

  firestoreRedux.save(`users/${userId}/user-ui`, {
    id: `utwd_${getIdWoPrefix({id: userId, prefix: 'usr_'})}`,
    type: 'use-template-welcome-dialog',
    userId,
    presented: true,
  }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
}

/**
 * Mark taskboardParticipantVideoWatched as true.
 */
function* markTaskboardParticipantVideoWatched() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  yield call(markWelcomeDialogAsPresented);

  firestoreRedux.save(`users/${userId}/user-ui`, {
    id: `tpv_${getIdWoPrefix({id: userId, prefix: 'usr_'})}`,
    type: 'taskboard-participant-video',
    userId,
    watched: true,
  }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
}

/**
 * Mark taskboardNonParticipantVideoWatched as true.
 */
function* markTaskboardNonParticipantVideoWatched() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  yield call(markWelcomeDialogAsPresented);

  firestoreRedux.save(`users/${userId}/user-ui`, {
    id: `tnpv_${getIdWoPrefix({id: userId, prefix: 'usr_'})}`,
    type: 'taskboard-non-participant-video',
    userId,
    watched: true,
  }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
}

/**
 * Mark whiteboardParticipantVideoWatched as true.
 */
function* markWhiteboardParticipantVideoWatched() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  yield call(markWelcomeDialogAsPresented);

  firestoreRedux.save(`users/${userId}/user-ui`, {
    id: `wpv_${getIdWoPrefix({id: userId, prefix: 'usr_'})}`,
    type: 'whiteboard-participant-video',
    userId,
    watched: true,
  }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
}

/**
 * Mark whiteboardNonParticipantVideoWatched as true.
 */
function* markWhiteboardNonParticipantVideoWatched() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  yield call(markWelcomeDialogAsPresented);

  firestoreRedux.save(`users/${userId}/user-ui`, {
    id: `wnpv_${getIdWoPrefix({id: userId, prefix: 'usr_'})}`,
    type: 'whiteboard-non-participant-video',
    userId,
    watched: true,
  }, { localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
}

/**
 * Resend invitation to given members for given board.
 */
function* resendInvitation(action) {
  const state = yield select();
  const boardId = action.boardId || router.selectors.dialogBoardId(state) || router.selectors.pageBoardId(state);
  const members = action.members || [];
  if(isEmpty(members)) {
    console.warn('Resend board invitation > empty members passed.');
    return;
  }

  try {
    yield call(requestApi, `/board/boards/${boardId}/resend-invitation`, { method: 'POST', body: { userIds: members } });
    showSnackBar({ message: i18next.t('board-page:teamToasts.resendInvitation') });
    yield put(actions._resendInvitationSuccess(boardId, members));
  } catch (error) {
    const code = error && error.code || 'UNKNOWN';
    yield put(actions._resendInvitationFailed(boardId, members, code));
    if(isNetworkError(error)){
      return;
    }
    console.error('Resend board invitation > failed due to this: ', error);
  }
}

function* changeRelativeDueDate(action) {
  const { boardId, value } = action;
  if(!boardId || !value) {
    console.error('Change relative due date > empty boardId or value passed.', { boardId, value });
    return;
  }
  
  try {
    yield call(requestApi, `/card/cards/relative-due-date`, { method: 'POST', body: { boardId, value } });
    yield put(actions._changeRelativeDueDateDone(boardId, value));
    showSnackBar({ message: i18next.t('board-setting:relativeDueDate.toast') });
  } catch (error) {
    const code = error && error.code || 'UNKNOWN';
    yield put(actions._changeRelativeDueDateFailed(boardId, value, code));
    if(isNetworkError(error)){
      return;
    }
    console.error('Change relative due date > failed due to this: ', error);
  }
}

/**
 * Marks `useCaseWelcomeDialogPresented` to true.
 */
function* markUseCaseWelcomeDialogAsPresented() {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const userId = auth.selectors.currentUserId(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);

  const presented = selectors.useCaseWelcomeDialogPresented(state, boardId);
  if (presented) {
    return;
  }

  if(userId) {
    const queryId = `user-board-ui_${boardId}_${userId}`;
    firestoreRedux.save(`users/${userId}/user-board-ui`, {
      id: `buuwd_${entityIdProvider.getId()}`,
      type: 'board-ui-usecase-welcome-dialog',
      userId,
      boardId,
      presented: true,
    }, { queryId, localWrite: impersonatedUser, remoteWrite: !impersonatedUser });
  } else {
    const key = `board-user-ui.${boardId}.useCaseWelcomeDialogPresented`;
    localStorage && localStorage.setItem && localStorage.setItem(key, true);
    window.dispatchEvent(new CustomEvent('storage-changed', { detail: { key, value: true } }));
  }
}


function* markImportBoardStatusAck({boardId}) {
  if(!boardId) {
    return;
  }

  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const attrs = selectors.attrs(state, boardId);
  const importBoardDetail = get(attrs, 'importBoardDetail') || {};
  if(!userId) {
    return;
  }

  try {
    const newImportBoardDetail = merge({}, importBoardDetail, {statusAck: true});
    firestoreRedux.save('boards', { ...attrs, ...{ importBoardDetail: newImportBoardDetail } }, { localWrite: true });
    yield call(requestApi, `/board/boards/${boardId}/import-board-status-ack`, { method: 'POST' });
  } catch (error) {
    firestoreRedux.save('boards', attrs, { localWrite: true });
    if(isNetworkError(error)) {
      return;
    }
    console.error('Failed to mark import board status acknowledge', error);
  }
}

/**
 * Manages PAGE_OPENED, PAGE_CLOSED & PAGE_CHANGED.
 */
 let previousPage;
 let previousBoardId;
 let previousDialogName;
 function* routeChangeHandler() {
  const state = yield select();
  const page = router.selectors.page(state);
  const currentPage = get(page, 'name');
  const currentBoardId = router.selectors.pageBoardId(state);
  const dialog = router.selectors.dialog(state);
  const currentDialogName = get(dialog, 'name');
  if (currentPage === 'BOARD') {
    if (previousPage !== currentPage) {
      yield put(actions._pageOpened(currentBoardId));
    } else {
      if (previousBoardId !== currentBoardId) {
        yield put(actions._pageChanged(currentBoardId, previousBoardId));
      }
    }

    // When card detail dialog opened through direct url, on close, loads board.
    if(currentDialogName !== 'CARD' && previousDialogName === 'CARD' && !selectors.loaded(state)){
      yield call(loadCurrentBoardDetail);
    }
  } else if (previousPage === 'BOARD') {
    yield put(actions._pageClosed(currentBoardId));
  }
  previousPage = currentPage;
  previousBoardId = currentBoardId;
  previousDialogName = currentDialogName;
}

/**
 * Watches router change.
 */
 function* watchRouter() {
  //If page is already opened, check once.
  yield call(routeChangeHandler);
  yield takeEvery(router.actions.UPDATE_ROUTER, routeChangeHandler);
}

/**
 * Init Saga.
 */
function* boardSaga() {
  yield all([
    call(watchRouter),
    call(subscribeSelectedColumn),
    takeEvery(actions.LOAD_CURRENT_BOARD, loadCurrentBoardDetail),
    takeEvery(actions.PAGE_OPENED, onPageOpened),
    takeEvery(actions.PAGE_CLOSED, onPageClosed),
    takeEvery(actions.PAGE_CHANGED, onPageChanged),
    takeEvery(actions.SUBSCRIBE_MEMBERS, subscribeBoardMembers),
    takeEvery(actions.UNSUBSCRIBE_MEMBERS, unsubscribeBoardMembers),
    takeEvery(actions.SUBSCRIBE_ACCOUNT_MEMBERS, subscribeAccountMembers),
    takeEvery(actions.UNSUBSCRIBE_ACCOUNT_MEMBERS, unsubscribeAccountMembers),
    takeEvery(actions.BOARD_TEAM_ADD_MEMBER, addMember),
    takeEvery(actions.BOARD_TEAM_CHANGE_MEMBER_ROLE, changeMemberRole),
    takeEvery(actions.BOARD_TEAM_REMOVE_MEMBER, removeMember),
    takeEvery(actions.BOARD_TEAM_LOAD_KNOWN_USERS, loadKnownUsers),
    takeEvery(actions.BOARD_TEAM_LOAD_ACCOUNT_SUBSCRIPTION, loadAccountSubscription),
    takeEvery(actions.BOARD_TEAM_DISCONNECT_KNOWN_USERS, disconnectKnownUsers),
    takeEvery(actions.LOAD_COLUMNS, loadColumns),
    takeEvery(actions.DISCONNECT_COLUMNS, disconnectColumns),
    takeEvery(actions.MARK_WELCOME_DIALOG_PRESENTED, markWelcomeDialogAsPresented),
    takeEvery(actions.UPDATE_BASIC_DETAIL, updateBoardBasicDetail),
    takeEvery(actions.BOARD_UPDATE_FAVORITE, updateFavorite),
    takeEvery(actions.BOARD_COPY, copy),
    takeEvery(actions.USE_CASE_BOARD_COPY, useCaseBoardCopy),
    takeEvery(actions.BOARDS_LOAD_BASIC_DETAILS, loadBoardsBasicDetails),
    takeEvery(actions.BOARDS_BASIC_DETAILS_DISCONNECT, disconnectBoardsBasicDetails),
    takeEvery(actions.OPEN, open),
    takeEvery(actions.CARD_TOGGLE_SELECTION, toggleCardSelection),
    takeEvery(actions.CARD_SELECTION_UPTO_CARD, uptoCardSelection),
    takeEvery(actions.SELECT_ALL, selectAllCards),
    takeEvery(actions.MARK_USE_TEMPLATE_WELCOME_DIALOG_PRESENTED, markUseTemplateWelcomeDialogAsPresented),
    takeEvery(actions.MARK_TASKBOARD_PARTICIPANT_VIDEO_WATCHED, markTaskboardParticipantVideoWatched),
    takeEvery(actions.MARK_TASKBOARD_NON_PARTICIPANT_VIDEO_WATCHED, markTaskboardNonParticipantVideoWatched),
    takeEvery(actions.MARK_WHITEBOARD_PARTICIPANT_VIDEO_WATCHED, markWhiteboardParticipantVideoWatched),
    takeEvery(actions.MARK_WHITEBOARD_NON_PARTICIPANT_VIDEO_WATCHED, markWhiteboardNonParticipantVideoWatched),
    takeEvery(actions.MARK_USE_CASE_WELCOME_DIALOG_PRESENTED, markUseCaseWelcomeDialogAsPresented),
    takeEvery(actions.BOARD_TEAM_RESEND_INVITATION, resendInvitation),
    takeEvery(actions.CHANGE_RELATIVE_DUE_DATE, changeRelativeDueDate),
    takeEvery(actions.CREATE_BOARD_AI, createBoardAI),
    takeEvery(actions.CREATE_TEMPLATE_AI, createTemplateAI),
    takeEvery(actions.MARK_IMPORT_BOARD_STATUS_ACKNOWLEDGE, markImportBoardStatusAck),
  ]);
}

export default boardSaga;